<template>
  <div class="image-upload-input">
    <van-uploader
      ref="uploader"
      :max-count="1"
      :after-read="afterRead"
      accept="image/*"
      :show-upload="false"
      v-model="fileList"
      @click-upload="handleClickUpload"
      :multiple="false"
    >
      <template #default>
        <div class="upload-trigger-wrapper">
          <!-- 工具栏样式 -->
          <div 
            v-if="showToolbarStyle" 
            :class="['toolbar-button', { 'toolbar-button--loading': isUploading }]"
            @touchstart="handleTouchStart"
            @touchend="handleTouchEnd"
            @touchcancel="handleTouchEnd"
          >
            <!-- 上传中的loading -->
            <svg v-if="isUploading" width="16" height="16" viewBox="0 0 24 24" fill="none" class="loading-spinner">
              <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-dasharray="31.4 31.4" fill="none"/>
            </svg>
            <!-- 默认OCR图片识别图标 -->
            <svg v-else width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M3 7V5C3 3.9 3.9 3 5 3H7" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
              <path d="M17 3H19C20.1 3 21 3.9 21 5V7" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
              <path d="M21 17V19C21 20.1 20.1 21 19 21H17" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
              <path d="M7 21H5C3.9 21 3 20.1 3 19V17" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
              <line x1="7" y1="8.5" x2="17" y2="8.5" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>
              <line x1="7" y1="12" x2="15" y2="12" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>
              <line x1="7" y1="15.5" x2="12" y2="15.5" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>
            </svg>
            <span class="toolbar-button__text">{{ fieldTitle || '图片识别' }}</span>
          </div>
          
          <!-- 原始图标样式 -->
          <div v-else class="upload-icon">
            <!-- 上传中的loading -->
            <svg v-if="isUploading" width="20" height="20" viewBox="0 0 24 24" fill="none" class="loading-spinner">
              <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-dasharray="31.4 31.4" fill="none"/>
            </svg>
            <!-- 默认文字提取图标（OCR扫描文档样式） -->
            <svg v-else width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M3 7V5C3 3.9 3.9 3 5 3H7" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
              <path d="M17 3H19C20.1 3 21 3.9 21 5V7" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
              <path d="M21 17V19C21 20.1 20.1 21 19 21H17" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
              <path d="M7 21H5C3.9 21 3 20.1 3 19V17" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
              <line x1="7" y1="8.5" x2="17" y2="8.5" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>
              <line x1="7" y1="12" x2="15" y2="12" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>
              <line x1="7" y1="15.5" x2="12" y2="15.5" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>
            </svg>
          </div>

          <transition name="ocr-tip-fade">
            <!-- <div v-if="false" class="ocr-onboarding-tip" @click.stop="dismissOnboardingTip" @touchstart.stop>
              <span>5秒内完成图片文本识别</span>
              <span class="ocr-onboarding-tip__close">&times;</span>
            </div> -->
          </transition>
        </div>
      </template>
    </van-uploader>
    
    <!-- OCR识别结果选择弹框 -->
      <van-popup
        v-model="showOcrDialog"
        position="bottom"
        :style="{ manHeight: '85%' }"
        closeable
        @closed="handleOcrDialogClosed"
        get-container="body"
      >
        <div class="ocr-dialog">
          <!-- 扫描阶段：显示图片和扫描动画 -->
          <div v-if="ocrStatus === 'scanning'" class="ocr-scanning">
            <div class="ocr-scanning__header">
              <h3>正在识别图片</h3>
              <p class="ocr-scanning__tip">请稍候...</p>
            </div>
            
            <div class="ocr-scanning__image-container">
              <img v-if="currentUploadedImage" :src="currentUploadedImage" alt="上传的图片" class="ocr-scanning__image" />
              <div v-else class="ocr-scanning__image ocr-scanning__placeholder"></div>
              
              <!-- 扫描线动画 -->
              <div class="scan-line" :key="'scan-line-' + scanKey"></div>
              
              <!-- 扫描遮罩 -->
              <div class="scan-overlay" :key="'scan-overlay-' + scanKey"></div>
            </div>
          </div>
          
          <!-- 选择阶段：显示识别结果 -->
          <div v-else-if="ocrStatus === 'completed'" class="ocr-selection">
            <!-- 标题区域（固定在顶部） -->
            <div class="ocr-dialog__header">
              <div class="ocr-dialog__title-row">
                <h3>识别结果</h3>
                <!--新版自动模式且未点击/未关闭引导时，用行动化提示替代默认文案 -->
                <transition name="ocr-detection-hint-fade" mode="out-in">
                  <p
                    v-if="ocrMode === 'auto' && useOcrV2 && ocrDetections.length > 0 && !hasClickedDetectionBox && !hasDismissedDetectionHint"
                    key="detection-hint"
                    class="ocr-dialog__tip ocr-dialog__tip--action"
                    @click.stop="dismissDetectionHint"
                  >
                    <van-icon name="guide-o" class="ocr-dialog__tip-icon" />
                    <span>点击图中蓝色词条框可直接选中</span>
                  </p>
                  <p v-else key="default-tip" class="ocr-dialog__tip">{{ ocrMode === 'auto' ? '可多选，点击选中/取消' : '完整识别的文本，可手动复制' }}</p>
                </transition>
              </div>
              
              <!-- 模式切换标签页 -->
              
            </div>
            
            
            <div class="ocr-selection__scrollable">
              <!-- 原图片预览（可点击查看大图） -->
              <div class="ocr-selection__image-preview" :class="{ 'ocr-selection__image-preview--v2': useOcrV2 && ocrDetections.length > 0 }">
                <div class="preview-image-wrapper" @click="handlePreviewImageClick">
                  <img
                    :src="currentUploadedImage"
                    alt="原图片"
                    class="preview-image"
                    @load="handlePreviewImageLoad"
                  />

                  <!-- 新版：坐标高亮框叠加层（按分词后子词渲染，支持独立点击选中） -->
                  <div
                    v-if="useOcrV2 && ocrSubDetections.length > 0 && ocrMode === 'auto'"
                    class="ocr-detection-overlay"
                    :class="{ 'ocr-detection-overlay--pulse': !hasClickedDetectionBox }"
                  >
                    <div
                      v-for="(sub, dIdx) in ocrSubDetections"
                      :key="'det-' + dIdx + '-' + sub.text"
                      class="ocr-detection-box"
                      :class="{ 'ocr-detection-box--active': isDetectionSelected(sub) }"
                      :style="getDetectionBoxStyle(sub)"
                      @click.stop="handleDetectionClick(sub, 'detection_box')"
                    >
                      <span class="ocr-detection-box__label">{{ sub.text }}</span>
                    </div>
                  </div>

                  <!-- 放大提示图标 -->
                  <div v-if="useOcrV2 && ocrDetections.length > 0" class="ocr-zoom-hint">
                    <van-icon name="expand-o" />
                  </div>
                </div>

                <!-- 重新拍摄按钮 -->
                <van-button
                  class="retake-photo-btn"
                  type="default"
                  icon="photograph"
                  size="mini"
                  round
                  @click="retakePhoto"
                  @touchstart.stop
                  @touchend.stop
                >重新上传</van-button>
              </div>
              <van-tabs 
                v-model="ocrMode" 
                class="ocr-mode-tabs mb20"
                animated
                swipeable
              >
                <van-tab name="auto" title="自动分词">
                  <template #title>
                    <div class="tab-title">
                      <van-icon name="apps-o" />
                      <span>自动分词</span>
                    </div>
                  </template>
                </van-tab>
                <van-tab name="manual" title="原始文本">
                  <template #title>
                    <div class="tab-title">
                      <van-icon name="notes-o" />
                      <span>原始文本</span>
                    </div>
                  </template>
                </van-tab>
              </van-tabs>
              
              <!-- 自动模式：显示选项列表 -->
              <div v-if="ocrMode === 'auto'" class="ocr-dialog__content mt20">
                <div
                  v-for="(word, index) in ocrWords"
                  :key="index"
                  class="ocr-word-item"
                  :class="{ 'ocr-word-item--active': selectedWords.includes(word) }"
                  @click="toggleWord(word, 'word_chip')"
                >
                  {{ word }}
                </div>
              </div>
              
              <!-- 手动模式：显示完整文本 -->
              <div v-else class="ocr-manual-text mt20">
                <div class="ocr-manual-text__content" @copy="handleManualCopy">{{ ocrRawText }}</div>
                <p class="ocr-manual-hint">以上为原始识别结果，支持手动选中并复制</p>

                <van-button 
                  type="primary" 
                  size="small" 
                  plain
                  @click="copyToClipboard"
                  class="ocr-copy-btn"
                  icon="copy-o"
                >
                  复制全部
                </van-button>
              </div>
            </div>
            
            <div class="ocr-dialog__footer">
              <!-- 自动模式显示确定按钮 -->
              <van-button 
                v-if="ocrMode === 'auto'" 
                block 
                type="primary" 
                @click="confirmSelection" 
                :disabled="selectedWords.length === 0"
              >
                确定 {{ selectedWords.length > 0 ? `(已选${selectedWords.length}个)` : '' }}
              </van-button>
              
              <!-- 手动模式显示关闭按钮 -->
              <van-button 
                v-else
                block 
                type="default" 
                @click="showOcrDialog = false"
              >
                关闭
              </van-button>
            </div>
          </div>
        </div>
      </van-popup>

      <!-- 新版OCR：全屏放大预览层（支持缩放/拖拽 + 高亮框点击选词） -->
      <!-- 注意：mounted 后会被移动到 document.body，避免被外层 dialog 的 stacking context 遮挡 -->
      <transition name="ocr-fullscreen-fade">
        <div
          v-if="showFullscreenPreview"
          ref="fullscreenPreview"
          class="ocr-fullscreen-preview"
          @click.self="closeFullscreenPreview"
          @touchmove.prevent
        >
          <!-- 顶部工具栏 -->
          <div class="ocr-fullscreen-preview__toolbar">
            <div class="ocr-fullscreen-preview__count">
              已选 {{ selectedWords.length }} / {{ ocrDetections.length }}
            </div>
            <van-icon
              name="cross"
              class="ocr-fullscreen-preview__close"
              @click="closeFullscreenPreview"
            />
          </div>

          <!-- 图片 + 高亮框缩放拖拽容器 -->
          <div
            class="ocr-fullscreen-preview__stage"
            ref="fullscreenStage"
            @touchstart="onFsTouchStart"
            @touchmove="onFsTouchMove"
            @touchend="onFsTouchEnd"
            @wheel.prevent="onFsWheel"
          >
            <div
              class="ocr-fullscreen-preview__transform"
              :style="fullscreenTransformStyle"
            >
              <div class="ocr-fullscreen-preview__image-wrap">
                <img
                  :src="currentUploadedImage"
                  class="ocr-fullscreen-preview__image"
                  draggable="false"
                  @load="handleFullscreenImageLoad"
                  @dragstart.prevent
                />
                <div class="ocr-detection-overlay ocr-detection-overlay--fs">
                  <div
                    v-for="(sub, dIdx) in ocrSubDetections"
                    :key="'fs-det-' + dIdx + '-' + sub.text"
                    class="ocr-detection-box ocr-detection-box--fs"
                    :class="{ 'ocr-detection-box--active': isDetectionSelected(sub) }"
                    :style="getDetectionBoxStyle(sub)"
                    @click.stop="handleDetectionClick(sub, 'detection_box_fs')"
                    @touchend.stop="onFsBoxTouchEnd($event, sub)"
                  >
                    <span class="ocr-detection-box__label ocr-detection-box__label--fs">{{ sub.text }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- 底部操作栏 -->
          <div class="ocr-fullscreen-preview__footer">
            <div class="ocr-fullscreen-preview__zoom-btns">
              <van-button size="mini" round icon="minus" @click="fsZoomOut" />
              <span class="ocr-fullscreen-preview__scale">{{ Math.round(fsScale * 100) }}%</span>
              <van-button size="mini" round icon="plus" @click="fsZoomIn" />
              <van-button size="mini" round icon="replay" @click="fsResetTransform" />
            </div>
            <van-button
              type="primary"
              size="small"
              round
              @click="closeFullscreenPreview"
            >完成</van-button>
          </div>
        </div>
      </transition>
  </div>
</template>

<script>
import { Toast, Uploader, Popup, Button, ImagePreview, Switch, Tabs, Tab, Icon } from 'vant';
import request from 'axios';
import store from '@/store';
import Vue from 'vue';

import { ocrImage } from '@/api/agent/agentApi';

export default {
  name: 'ImageUploadInput',
  
  components: {
    [Uploader.name]: Uploader,
    [Popup.name]: Popup,
    [Button.name]: Button,
    [Switch.name]: Switch,
    [Tabs.name]: Tabs,
    [Tab.name]: Tab,
    [Icon.name]: Icon
  },
  
  props: {
    // 当前字段值
    value: {
      type: String,
      default: ''
    },
    // 字段名称
    fieldName: {
      type: String,
      default: ''
    },
    // 字段标题
    fieldTitle: {
      type: String,
      default: ''
    },
    // 最大上传数量
    maxCount: {
      type: Number,
      default: 9
    },
    // 上传接口地址
    action: {
      type: String,
      default: '/relay/cosfile/index'
    },
    // 是否使用新的微信选图方式
    newChooseImage: {
      type: Boolean,
      default: false
    },
    // OCR识别结果回调函数，用于处理识别结果
    // 参数: (ocrResult: string, words: string[]) => string[] | Promise<string[]>
    // 返回: 处理后的文字数组
    ocrCallback: {
      type: Function,
      default: null
    },
    // OCR识别的额外提示词
    extraPrompt: {
      type: String,
      default: ''
    },
    // 是否显示为工具栏样式
    showToolbarStyle: {
      type: Boolean,
      default: false
    },
    // 流程标识（用于 Aegis 上报）
    processDefinitionKey: {
      type: String,
      default: ''
    }
  },
  
  data() {
    return {
      isUploading: false,
      wxConfigured: false,
      agentConfigured: false,
      configPromise: null,
      fileList: [],
      shouldTriggerNativeUpload: false,
      // OCR相关
      showOcrDialog: false,
      ocrWords: [],
      selectedWords: [],
      ocrStatus: 'scanning', // scanning: 扫描中, completed: 完成
      currentUploadedImage: '', // 当前上传的图片URL
      pendingImageFile: null, // 待上传的图片文件
      ocrMode: 'auto', // 识别模式：'auto' 自动模式（切分选项）, 'manual' 手动模式（展示完整文本）
      ocrRawText: '', // 原始识别文本（用于手动模式）
      hasSwitchedToManual: false, // 是否切换过"原始文本"tab
      hasCopiedManually: false, // 是否在"原始文本"tab手动复制过
      showOnboardingTip: false, // 是否显示气泡提示
      tipTimer: null, // 气泡提示自动消失定时器
      scanKey: 0, // 扫描动画重置键
      // 新版 OCR 相关
      ocrDetections: [], // 新版OCR识别结果原始数据（含坐标）：[{ text, polygon:[{x,y}], itemPolygon }]
      imageNaturalSize: { width: 0, height: 0 }, // 原图自然尺寸
      imagePreviewSize: { width: 0, height: 0 }, // 预览区域实际渲染尺寸
      hasClickedDetectionBox: false, // 本轮识别中是否点击过图上蓝色词条框（用于关闭引导提示和呼吸动效）
      hasDismissedDetectionHint: false, // 本轮识别中是否已手动关闭顶部引导提示
      // 全屏放大预览相关
      showFullscreenPreview: false,
      fsScale: 1,
      fsTranslate: { x: 0, y: 0 },
      // 手势状态（不放入 data 以避免响应式开销的字段用 $ 前缀在 methods 中挂到 this）
      fsGesture: {
        isDragging: false,
        isPinching: false,
        startX: 0,
        startY: 0,
        startTranslateX: 0,
        startTranslateY: 0,
        pinchStartDist: 0,
        pinchStartScale: 1,
        moved: false,
        touchStartAt: 0
      }
    };
  },
  
  computed: {
    // 检查是否在微信/企微环境中
    isWechatEnv() {
      if (typeof window === 'undefined') return false;
      const ua = window.navigator.userAgent.toLowerCase();
      return ua.includes('micromessenger') || ua.includes('wxwork');
    },
    
    // 是否启用新的企微OCR上传模式
    useNewOCR() {
      try {
        return store.getters.getConfigItem('useNewOCR') === '1';
      } catch (e) {
        return false;
      }
    },
    
    // 是否应该使用微信API（useNewOCR开启 且 在微信环境中）
    shouldUseWechatApi() {
      return this.useNewOCR;
    },

    // 是否启用新版 OCR（含坐标信息的 TextDetections 结构）
    useOcrV2() {
      try {
        return store.getters.getConfigItem('useOcrV2') === '1';
      } catch (e) {
        return false;
      }
    },

    // 新版 OCR 应用标识（可通过配置覆盖）
    ocrV2FlowKey() {
      try {
        return store.getters.getConfigItem('ocrV2FlowKey') || 'ocr_photo_new_copy_copy';
      } catch (e) {
        return 'ocr_photo_new_copy_copy';
      }
    },

    // 全屏预览层图片 + 高亮框整体的 transform
    fullscreenTransformStyle() {
      return {
        transform: `translate3d(${this.fsTranslate.x}px, ${this.fsTranslate.y}px, 0) scale(${this.fsScale})`,
        transformOrigin: 'center center',
        transition: this.fsGesture.isDragging || this.fsGesture.isPinching
          ? 'none'
          : 'transform 0.18s ease'
      };
    },

    /**
     * 新版OCR的实际渲染高亮框列表：
     * 每条 detection 会按 v1 分词规则被拆分为多个子词，
     * 并按字符占比切分外接矩形，得到多个可独立点击的子高亮框。
     * 结构：[{ text, detection, box, isFullBox }]
     */
    ocrSubDetections() {
      if (!this.useOcrV2 || !this.ocrDetections || this.ocrDetections.length === 0) {
        return [];
      }
      if (!this.imageNaturalSize.width || !this.imageNaturalSize.height) {
        return [];
      }
      const list = [];
      for (const det of this.ocrDetections) {
        const subs = this.buildSubDetectionsFor(det);
        for (const s of subs) list.push(s);
      }
      return list;
    }
  },
  
  mounted() {
    // 气泡提示改为点击图标时展示，不再自动弹出
  },
  
  beforeDestroy() {
    this.dismissOnboardingTip();
    // 清理可能已移到 body 的全屏预览节点，避免残留
    this.removeFullscreenPreviewFromBody();
  },
  
  watch: {
    // 监听识别模式切换
    ocrMode(newMode, oldMode) {
      // 只在真正切换时上报（避免初始化时上报）
      if (oldMode && newMode !== oldMode) {
        // 记录是否切换过"原始文本"tab
        if (newMode === 'manual') {
          this.hasSwitchedToManual = true;
        }
        // Aegis打点：用户切换识别模式
        this.reportAegis({
          action: 'ocr_mode_switch',
          fromMode: oldMode,
          toMode: newMode,
          modeLabel: newMode === 'auto' ? '智能识别' : '原始文本',
          hasSelectedWords: this.selectedWords.length > 0,
          selectedWordsCount: this.selectedWords.length,
          totalWordsCount: this.ocrWords.length,
          rawTextLength: this.ocrRawText.length
        });
      }
    }
  },
  
  methods: {
    // 尝试展示首次使用气泡提示（点击图标时调用）
    tryShowOnboardingTip() {
      // 如果已经在展示则不重复触发
      if (this.showOnboardingTip) return;
      
      this.showOnboardingTip = true;
      
      // 清除之前的定时器
      if (this.tipTimer) {
        clearTimeout(this.tipTimer);
      }
      // 5秒后自动消失
      this.tipTimer = setTimeout(() => {
        this.dismissOnboardingTip();
      }, 5000);
    },
    
    // 关闭气泡提示
    dismissOnboardingTip() {
      this.showOnboardingTip = false;
      if (this.tipTimer) {
        clearTimeout(this.tipTimer);
        this.tipTimer = null;
      }
    },
    
    /**
     * 将字符串格式的正则表达式转换为真正的正则对象
     * @param {String|RegExp} regexStr - 字符串格式的正则（如 "/[：:]/g"）或正则对象
     * @returns {RegExp|null} 正则对象或 null
     */
    parseRegex(regexStr) {
      // 如果已经是正则对象，直接返回
      if (regexStr instanceof RegExp) {
        return regexStr;
      }
      
      // 如果不是字符串或为空，返回 null
      if (typeof regexStr !== 'string' || !regexStr) {
        return null;
      }
      
      try {
        // 匹配正则表达式格式：/pattern/flags
        const matchRegex = new RegExp('^\\/(.*?)\\/([gimsuy]*)$');
        const match = regexStr.match(matchRegex);
        
        if (match) {
          // 提取 pattern 和 flags
          const pattern = match[1];
          const flags = match[2];
          
          // 验证 flags 是否有效（iOS 兼容性）
          // 移除不支持的 flag（如 v flag 在 iOS 17 以下不支持）
          const validFlags = flags.split('').filter(flag => {
            return ['g', 'i', 'm', 's', 'u', 'y'].includes(flag);
          }).join('');
          
          return new RegExp(pattern, validFlags);
        }
        
        // 如果不是标准格式，尝试直接作为 pattern 创建正则（不带 flags）
        return new RegExp(regexStr);
      } catch (error) {
        console.error('[parseRegex] 正则表达式解析失败:', regexStr, error);
        return null;
      }
    },
    
    // 处理触摸开始（移动端）
    handleTouchStart(event) {
      if (this.showToolbarStyle && event.currentTarget) {
        event.currentTarget.style.background = '#e6e6e6';
      }
    },
    
    // 处理触摸结束（移动端）
    handleTouchEnd(event) {
      if (this.showToolbarStyle && event.currentTarget) {
        event.currentTarget.style.background = '#f7f8fa';
      }
    },
    
    // 提前触发企微API初始化（在入口处调用，不阻塞UI）
    startWxConfigPreinit() {
      if (this._wxConfigPreinitPromise) return; // 避免重复初始化
      this._wxConfigPreinitPromise = this.ensureWechatConfig().catch(err => {
        console.warn('[ImageUploadInput] 企微API预初始化失败，将降级为浏览器上传:', err);
        this.reportAegis({ action: 'wx_config_preinit_failed', error: err.message || String(err) });
        return false;
      });
    },
    
    // 点击处理
    async handleClick(e) {
      if (this.isUploading) return;
      
      // 点击时展示气泡提示
      this.tryShowOnboardingTip();
      
      // Aegis打点：用户点击OCR图标
      this.reportAegis({
        action: 'ocr_icon_clicked',
        entry: 'handleClick',
        shouldUseWechatApi: this.shouldUseWechatApi,
        isWechatEnv: this.isWechatEnv
      });
      
      // useNewOCR 启用时，拦截默认上传，改为企微API方式
      if (this.shouldUseWechatApi) {
        // 阻止事件冒泡，防止触发 van-uploader 的默认文件选择
        if (e) {
          e.stopPropagation();
          e.preventDefault();
        }
        // 提前触发企微API初始化
        this.startWxConfigPreinit();
        await this.handleWechatUpload();
        return;
      }
      
      // Aegis打点：使用浏览器默认上传
      this.reportAegis({
        action: 'ocr_browser_upload_entry',
        entry: 'handleClick'
      });
      
      // 未启用 useNewOCR 时，手动触发 van-uploader 的文件选择
      if (e) {
        e.stopPropagation();
        e.preventDefault();
      }
      this.$nextTick(() => {
        const uploaderEl = this.$refs.uploader && this.$refs.uploader.$el;
        if (uploaderEl) {
          const input = uploaderEl.querySelector('.van-uploader__input');
          if (input) {
            input.click();
          }
        }
      });
    },
    
    // van-uploader 点击上传事件（与 Upload.js 中 click-upload 处理方式完全一致）
    handleClickUpload(e) {
      if (this.isUploading) return;
      
      // 点击时展示气泡提示
      this.tryShowOnboardingTip();
      
      // Aegis打点：用户点击OCR图标
      this.reportAegis({
        action: 'ocr_icon_clicked',
        entry: 'handleClickUpload',
        shouldUseWechatApi: this.shouldUseWechatApi,
        isWechatEnv: this.isWechatEnv
      });
      
      // shouldUseWechatApi 启用时，拦截默认文件选择，显示选择对话框
      if (this.shouldUseWechatApi && !this.shouldTriggerNativeUpload) {
        e.stopPropagation();
        e.preventDefault();
        // 提前触发企微API初始化
        this.startWxConfigPreinit();
        this.handleWechatUpload(); // 显示选择对话框
        return;
      }
      // 未启用时，什么都不做，让 Vant 组件的默认行为执行（触发文件选择器）
    },
    
    // van-uploader 的 afterRead 回调
    async afterRead(file) {
      const files = Array.isArray(file) ? file : [file];
      const fileObj = files[0].file;
      
      // 立即显示弹框，预览选中的图片
      this.pendingImageFile = fileObj;
      this.currentUploadedImage = URL.createObjectURL(fileObj);
      this.ocrStatus = 'scanning';
      this.scanKey++;
      this.showOcrDialog = true;
      
      // 立即清空 fileList，避免显示预览
      this.fileList = [];
      
      // Aegis打点：文件上传开始
      this.reportAegis({
        action: 'image_upload_start',
        uploadMethod: 'browser',
        fileName: fileObj.name,
        fileSize: fileObj.size,
        fileType: fileObj.type
      });
      
      // 然后进行上传
      await this.uploadFiles([fileObj]);
    },
    
    // 上传文件
    async uploadFiles(files) {
      try {
        this.isUploading = true;
        
        const uploadedUrls = [];
        
        for (let i = 0; i < files.length; i++) {
          const file = files[i];
          
          const reqData = new FormData();
          reqData.append('file', file);
          
          const { data } = await request.post(this.action, reqData, {
            headers: {
              'Content-Type': 'multipart/form-data',
              authToken: sessionStorage.getItem('AuthToken'),
              userid: sessionStorage.getItem('userid'),
            },
          });
          
          if (data?.FileList?.length) {
            uploadedUrls.push(...data.FileList.map(item => item.url));
          }
        }
        
        if (uploadedUrls.length > 0) {
          // Aegis打点：文件上传成功
          this.reportAegis({
            action: 'image_upload_success',
            uploadMethod: 'browser',
            uploadedUrl: uploadedUrls[0],
            fileCount: uploadedUrls.length
          });
          
          // 触发上传完成事件，传递上传的图片URL
          this.$emit('upload-complete', uploadedUrls);
          
          // 上传成功后，更新为真实的图片URL并进行OCR识别
          this.currentUploadedImage = uploadedUrls[0];
          
          // 开始OCR识别
          this.performOcr(uploadedUrls[0]);
        } else {
          // Aegis打点：文件上传失败（无返回URL）
          this.reportAegis({
            action: 'image_upload_failed',
            uploadMethod: 'browser',
            reason: 'no_urls_returned'
          });
          
          // 上传失败，关闭弹框
          Toast.fail('上传失败，请重试');
          this.showOcrDialog = false;
        }
        
      } catch (e) {
        console.error('上传失败:', e);
        
        // Aegis打点：文件上传异常
        this.reportAegis({
          action: 'image_upload_error',
          uploadMethod: 'browser',
          error: e.message || String(e)
        });
        
        Toast.fail('上传失败，请稍后再试');
        // 上传失败，关闭弹框
        this.showOcrDialog = false;
      } finally {
        this.isUploading = false;
      }
    },
    
    // 微信方式上传（3选1入口）
    async handleWechatUpload() {
      try {
        // 使用入口处已提前触发的初始化 promise（handleClick/handleClickUpload 中已调用 startWxConfigPreinit）
        const wxConfigPromise = this._wxConfigPreinitPromise || this.ensureWechatConfig().catch(err => {
          console.warn('[ImageUploadInput] 企微API预初始化失败，将降级为浏览器上传:', err);
          this.reportAegis({ action: 'wx_config_preinit_failed', error: err.message || String(err) });
          return false;
        });

        // 同时显示选择对话框：拍照 / 相册 / 企微对话
        const uploadMethod = await this.showUploadMethodDialog();
        
        // 用户选择后，等待初始化完成（初始化在用户看选项期间已并行进行）
        const configToast = Toast.loading({
          message: '初始化中...',
          forbidClick: true,
          duration: 0
        });
        
        try {
          const configured = await wxConfigPromise;
          configToast.clear();
          // 清理预初始化 promise，以便下次重新触发
          this._wxConfigPreinitPromise = null;
          
          if (!configured) {
            // 企微API初始化失败，降级为浏览器默认上传
            this.reportAegis({ action: 'wx_config_init_failed_fallback_browser', reason: 'ensureWechatConfig_returned_false', uploadMethod });
            
            // 降级：触发浏览器原生文件选择
            this.triggerBrowserFileInput();
            return;
          }
          
          await new Promise(resolve => setTimeout(resolve, 300));
          
          if (uploadMethod === 'camera') {
            // 拍照模式
            await this.handleWechatImageUploadBySourceType(['camera']);
          } else if (uploadMethod === 'album') {
            // 相册模式
            await this.handleWechatImageUploadBySourceType(['album']);
          } else if (uploadMethod === 'wechat-file') {
            // 从企微对话选择
            await this.handleWechatFileUpload();
          }
        } catch (err) {
          configToast.clear();
          if (err.message !== '用户取消选择') {
            // Aegis打点：企微API调用流程异常
            this.reportAegis({
              action: 'wx_upload_flow_error',
              uploadMethod: uploadMethod,
              error: err.message || String(err)
            });
            Toast.fail(err.message || '选择文件失败');
          }
        }
      } catch (err) {
        // 用户取消选择方式，清理预初始化 promise
        this._wxConfigPreinitPromise = null;
      }
    },
    
    // 降级：关闭弹框 + 标记为浏览器原生上传模式 + 提示用户重新点击
    triggerBrowserFileInput() {
      this.reportAegis({ action: 'fallback_browser_upload_triggered' });
      // 关闭可能存在的选择上传方式弹框（ActionSheet 是独立 Vue 实例，需要通过 DOM 强制移除）
      this.dismissUploadMethodDialog();
      // 关闭 OCR 弹框
      this.showOcrDialog = false;
      // 标记降级，下次点击时 handleClickUpload 不再拦截，直接走浏览器原生上传
      this.shouldTriggerNativeUpload = true;
      Toast('已根据机型进行优化，请重新点击图片上传');
    },
    
    // 强制关闭上传方式选择弹框（ActionSheet）
    dismissUploadMethodDialog() {
      if (this._actionSheetVm) {
        try {
          this._actionSheetVm.show = false;
          this._actionSheetVm.$nextTick(() => {
            this._actionSheetVm.$destroy();
            const parentNode = this._actionSheetVm.$el && this._actionSheetVm.$el.parentNode;
            if (parentNode) {
              parentNode.removeChild(this._actionSheetVm.$el);
            }
            this._actionSheetVm = null;
          });
        } catch (e) {
          this._actionSheetVm = null;
        }
      }
    },
    
    // 显示上传方式选择对话框（3种企微模式）
    showUploadMethodDialog() {
      const self = this;
      // 从配置获取描述文案，若未配置则使用默认值
      let ocrDesc = '支持把图片中的文字提取出来，并快速填入工单。\n请选择图片来源：';
      try {
        const configDesc = store.getters.getConfigItem('OCRDesc');
        if (configDesc) {
          ocrDesc = configDesc;
        }
      } catch (e) {
        // 使用默认描述
      }
      
      return new Promise((resolve, reject) => {
        const actionSheetVm = new Vue({
          data() {
            return {
              show: true,
              actions: [
                { name: '拍照', value: 'camera' },
                { name: '从相册选择', value: 'album' },
                // { name: '从企微对话中选择', value: 'wechat-file' }
              ]
            };
          },
          methods: {
            onSelect(item) {
              this.show = false;
              this.$nextTick(() => {
                this.$destroy();
                const parentNode = this.$el.parentNode;
                if (parentNode) {
                  parentNode.removeChild(this.$el);
                }
                self._actionSheetVm = null;
                // Aegis打点：用户选择图片来源
                self.reportAegis({
                  action: 'ocr_source_selected',
                  source: item.value,
                  sourceName: item.name
                });
                resolve(item.value);
              });
            },
            onCancel() {
              this.show = false;
              this.$nextTick(() => {
                this.$destroy();
                const parentNode = this.$el.parentNode;
                if (parentNode) {
                  parentNode.removeChild(this.$el);
                }
                self._actionSheetVm = null;
                // Aegis打点：用户取消选择图片来源
                self.reportAegis({
                  action: 'ocr_source_cancelled'
                });
                reject(new Error('用户取消选择'));
              });
            }
          },
          render(h) {
            return h('van-action-sheet', {
              props: {
                value: this.show,
                actions: this.actions,
                title: '文字提取工具',
                description: ocrDesc,
                cancelText: '取消'
              },
              on: {
                input: (val) => { 
                  if (!val) this.onCancel(); 
                },
                select: this.onSelect,
                cancel: this.onCancel
              }
            });
          }
        });

        // 保存引用，以便降级时能强制关闭
        self._actionSheetVm = actionSheetVm;
        
        const mountNode = document.createElement('div');
        document.body.appendChild(mountNode);
        actionSheetVm.$mount(mountNode);
      });
    },
    
    // 配置微信SDK
    async ensureWechatConfig() {
      if (!this.isWechatEnv) return false;
      if (this.wxConfigured) return true;
      if (this.configPromise) return await this.configPromise;
      
      this.configPromise = (async () => {
        try {
          // Aegis打点：开始初始化微信SDK
          this.reportAegis({ action: 'wx_sdk_config_start' });

          const timestamp = Math.round(new Date() / 1000) + '';
          const configData = {
            Url: window.location.href,
            Noncestr: 'otob',
            Timestamp: timestamp
          };

          const signatureUrl = store.getters.getConfigItem('signature');
          const { data } = await request.post(signatureUrl, configData);

          const wxApi = typeof ww !== 'undefined' ? ww : wx;
          
          await new Promise((resolve, reject) => {
            const timeout = setTimeout(() => {
              // Aegis打点：微信SDK配置超时
              this.reportAegis({ action: 'wx_sdk_config_timeout' });
              reject(new Error('微信 SDK 配置超时'));
            }, 10000);

            wxApi.config({
              beta: true,
              debug: false,
              appId: sessionStorage.getItem('appid') || store.getters.getConfigItem('corpID'),
              timestamp: parseInt(timestamp),
              nonceStr: 'otob',
              signature: data.signature,
              jsApiList: ['chooseImage', 'getLocalImgData', 'agentConfig']
            });

            wxApi.ready(async () => {
              clearTimeout(timeout);
              this.wxConfigured = true;

              // Aegis打点：微信SDK wx.config 成功
              this.reportAegis({ action: 'wx_sdk_config_success' });
              
              const ua = window.navigator.userAgent.toLowerCase();
              const isWxWork = ua.includes('wxwork');
              
              if (isWxWork) {
                try {
                  const signatureApiUrl = store.getters.getConfigItem('signatureApi');
                  
                  if (!signatureApiUrl) {
                    console.warn('signatureApi 未配置，跳过 agentConfig 配置');
                    resolve(true);
                    return;
                  }
                  
                  const verifyTimestamp = `${Math.round(new Date() / 1000)}`;
                  const verifyData = {
                    Url: window.location.href,
                    Noncestr: 'otob',
                    Timestamp: verifyTimestamp,
                  };

                  const verifyRes = await request.post(signatureApiUrl, verifyData, {
                    headers: {
                      authToken: sessionStorage.getItem('AuthToken'),
                      userid: sessionStorage.getItem('userid')
                    }
                  });

                  if (!verifyRes?.data?.signature) {
                    console.warn('agentConfig 签名获取失败，跳过 agentConfig 配置');
                    resolve(true);
                    return;
                  }
                  
                  const appsignature = verifyRes.data.signature;
                  
                  await new Promise((agentResolve) => {
                    wxApi.agentConfig({
                      beta: true,
                      corpid: store.getters.getConfigItem('corpID'),
                      agentid: process.env.NODE_ENV === 'development' ? '1000036' : '1000035',
                      timestamp: verifyTimestamp * 1,
                      nonceStr: 'otob',
                      signature: appsignature,
                      jsApiList: ['selectExternalContact', 'saveApprovalSelectedItems', 'getApprovalSelectedItems', 'chooseMessageFile', 'getLocalFileData'],
                      success: () => {
                        this.agentConfigured = true;
                        // Aegis打点：企微 agentConfig 成功
                        this.reportAegis({ action: 'wx_agent_config_success' });
                        agentResolve(true);
                      },
                      fail: (agentErr) => {
                        this.agentConfigured = false;
                        // Aegis打点：企微 agentConfig 失败
                        this.reportAegis({
                          action: 'wx_agent_config_failed',
                          error: agentErr?.errMsg || String(agentErr || '')
                        });
                        agentResolve(false);
                      }
                    });
                  });
                  
                  resolve(true);
                } catch (e) {
                  this.agentConfigured = false;
                  // Aegis打点：获取 agentConfig 签名异常
                  this.reportAegis({ action: 'wx_agent_config_error', error: 'agent_ticket_exception' });
                  resolve(true);
                }
              } else {
                resolve(true);
              }
            });

            wxApi.error((err) => {
              clearTimeout(timeout);
              // Aegis打点：微信SDK wx.config 失败
              this.reportAegis({
                action: 'wx_sdk_config_error',
                error: err.errMsg || '未知错误'
              });
              reject(new Error('微信配置错误: ' + (err.errMsg || '未知错误')));
            });
          });

          return true;
        } catch (err) {
          throw err;
        } finally {
          this.configPromise = null;
        }
      })();

      return await this.configPromise;
    },
    
    // 微信图片选择并上传（按 sourceType 区分拍照/相册）
    async handleWechatImageUploadBySourceType(sourceType) {
      const wxApi = typeof ww !== 'undefined' ? ww : wx;
      
      const localIds = await new Promise((resolve, reject) => {
        wxApi.chooseImage({
          count: 1,
          sizeType: ['original', 'compressed'],
          sourceType: sourceType,
          defaultCameraMode: 'normal',
          success: (res) => resolve(res.localIds || []),
          fail: (err) => {
            // Aegis打点：chooseImage API 调用失败
            this.reportAegis({
              action: 'wx_choose_image_failed',
              sourceType: sourceType[0],
              error: err?.errMsg || String(err || '')
            });
            reject(err);
          },
          cancel: () => {
            // Aegis打点：用户取消选择图片
            this.reportAegis({
              action: 'wx_choose_image_cancelled',
              sourceType: sourceType[0]
            });
            reject(new Error('用户取消选择'));
          }
        });
      });
      
      if (!localIds || localIds.length === 0) return;
      
      // 立即展示"扫描中"弹窗，无需等待图片数据获取
      this.currentUploadedImage = '';
      this.ocrStatus = 'scanning';
      this.scanKey++;
      this.showOcrDialog = true;
      
      // 异步获取图片数据并更新预览
      this.getLocalImageData(localIds[0]).then(base64Data => {
        this.currentUploadedImage = base64Data;
      }).catch(err => {
        console.error('获取图片预览数据失败:', err);
      });
      
      // Aegis打点
      this.reportAegis({
        action: 'image_upload_start',
        uploadMethod: `wechat-${sourceType[0]}`,
        imageCount: localIds.length
      });
      
      this.isUploading = true;
      
      try {
        const uploadedUrls = [];
        
        for (let i = 0; i < localIds.length; i++) {
          const base64Data = await this.getLocalImageData(localIds[i]);
          const fileName = `wechat_image_${Date.now()}_${i}.jpg`;
          const file = this.base64ToFile(base64Data, fileName);
          
          const reqData = new FormData();
          reqData.append('file', file);
          
          const { data } = await request.post(this.action, reqData, {
            headers: {
              'Content-Type': 'multipart/form-data',
              authToken: sessionStorage.getItem('AuthToken'),
              userid: sessionStorage.getItem('userid'),
            },
          });
          
          if (data?.FileList?.length) {
            uploadedUrls.push(...data.FileList.map(item => item.url));
          }
        }
        
        if (uploadedUrls.length > 0) {
          this.reportAegis({
            action: 'image_upload_success',
            uploadMethod: `wechat-${sourceType[0]}`,
            uploadedUrl: uploadedUrls[0],
            fileCount: uploadedUrls.length
          });
          
          this.$emit('upload-complete', uploadedUrls);
          this.currentUploadedImage = uploadedUrls[0];
          this.performOcr(uploadedUrls[0]);
        } else {
          this.reportAegis({
            action: 'image_upload_failed',
            uploadMethod: `wechat-${sourceType[0]}`,
            reason: 'no_urls_returned'
          });
          
          Toast.fail('上传失败，请重试');
          this.showOcrDialog = false;
        }
      } catch (err) {
        console.error('微信图片上传失败:', err);
        
        this.reportAegis({
          action: 'image_upload_error',
          uploadMethod: `wechat-${sourceType[0]}`,
          error: err.message || String(err)
        });
        
        Toast.fail('上传失败，请重试');
        this.showOcrDialog = false;
      } finally {
        this.isUploading = false;
      }
    },
    
    // 微信图片选择并上传（兼容旧逻辑，sourceType 为 album+camera）
    async handleWechatImageUpload() {
      const wxApi = typeof ww !== 'undefined' ? ww : wx;
      
      const localIds = await new Promise((resolve, reject) => {
        wxApi.chooseImage({
          count: 1, // 只允许选择1张图片
          sizeType: ['original', 'compressed'],
          sourceType: ['album', 'camera'],
          defaultCameraMode: 'normal',
          success: (res) => resolve(res.localIds || []),
          fail: (err) => {
            // Aegis打点：chooseImage API 调用失败（兼容模式）
            this.reportAegis({
              action: 'wx_choose_image_failed',
              sourceType: 'album+camera',
              error: err?.errMsg || String(err || '')
            });
            reject(err);
          },
          cancel: () => {
            // Aegis打点：用户取消选择图片（兼容模式）
            this.reportAegis({
              action: 'wx_choose_image_cancelled',
              sourceType: 'album+camera'
            });
            reject(new Error('用户取消选择'));
          }
        });
      });
      
      if (!localIds || localIds.length === 0) return;
      
      // 立即展示"扫描中"弹窗，无需等待图片数据获取
      this.currentUploadedImage = '';
      this.ocrStatus = 'scanning';
      this.scanKey++;
      this.showOcrDialog = true;
      
      // 异步获取图片数据并更新预览
      this.getLocalImageData(localIds[0]).then(base64Data => {
        this.currentUploadedImage = base64Data;
      }).catch(err => {
        console.error('获取图片预览数据失败:', err);
      });
      
      // Aegis打点：文件上传开始（微信方式）
      this.reportAegis({
        action: 'image_upload_start',
        uploadMethod: 'wechat',
        imageCount: localIds.length
      });
      
      this.isUploading = true;
      
      try {
        const uploadedUrls = [];
        
        for (let i = 0; i < localIds.length; i++) {
          const base64Data = await this.getLocalImageData(localIds[i]);
          const fileName = `wechat_image_${Date.now()}_${i}.jpg`;
          const file = this.base64ToFile(base64Data, fileName);
          
          const reqData = new FormData();
          reqData.append('file', file);
          
          const { data } = await request.post(this.action, reqData, {
            headers: {
              'Content-Type': 'multipart/form-data',
              authToken: sessionStorage.getItem('AuthToken'),
              userid: sessionStorage.getItem('userid'),
            },
          });
          
          if (data?.FileList?.length) {
            uploadedUrls.push(...data.FileList.map(item => item.url));
          }
        }
        
        if (uploadedUrls.length > 0) {
          // Aegis打点：文件上传成功（微信方式）
          this.reportAegis({
            action: 'image_upload_success',
            uploadMethod: 'wechat',
            uploadedUrl: uploadedUrls[0],
            fileCount: uploadedUrls.length
          });
          
          this.$emit('upload-complete', uploadedUrls);
          
          // 上传成功后，更新为真实的图片URL并进行OCR识别
          this.currentUploadedImage = uploadedUrls[0];
          
          // 开始OCR识别
          this.performOcr(uploadedUrls[0]);
        } else {
          // Aegis打点：文件上传失败（微信方式）
          this.reportAegis({
            action: 'image_upload_failed',
            uploadMethod: 'wechat',
            reason: 'no_urls_returned'
          });
          
          // 上传失败，关闭弹框
          Toast.fail('上传失败，请重试');
          this.showOcrDialog = false;
        }
      } catch (err) {
        console.error('微信图片上传失败:', err);
        
        // Aegis打点：文件上传异常（微信方式）
        this.reportAegis({
          action: 'image_upload_error',
          uploadMethod: 'wechat',
          error: err.message || String(err)
        });
        
        Toast.fail('上传失败，请重试');
        this.showOcrDialog = false;
      } finally {
        this.isUploading = false;
      }
    },
    
    // 企微文件选择并上传
    async handleWechatFileUpload() {
      const ua = window.navigator.userAgent.toLowerCase();
      const isWxWork = ua.includes('wxwork');
      
      if (!isWxWork) {
        // Aegis打点：非企微环境尝试使用企微文件选择
        this.reportAegis({ action: 'wx_file_upload_env_error', reason: 'not_wxwork' });
        Toast.fail('当前不在企业微信环境中');
        return;
      }
      
      if (!this.agentConfigured) {
        // Aegis打点：agentConfig 未配置
        this.reportAegis({ action: 'wx_file_upload_env_error', reason: 'agent_not_configured' });
        Toast.fail('企微应用权限未配置');
        return;
      }
      
      const wxApi = typeof wx !== 'undefined' ? wx : (typeof window.wx !== 'undefined' ? window.wx : null);
      
      if (!wxApi) {
        // Aegis打点：企微 SDK 未加载
        this.reportAegis({ action: 'wx_file_upload_env_error', reason: 'sdk_not_loaded' });
        Toast.fail('企业微信 SDK 未加载');
        return;
      }
      
      const tempFiles = await new Promise((resolve, reject) => {
        wxApi.invoke(
          'chooseMessageFile',
          {
            count: 1, // 只允许选择1张图片
            type: 'image'
          },
          (res) => {
            if (res.err_msg === 'chooseMessageFile:ok') {
              resolve(res.tempFiles || []);
            } else if (res.err_msg === 'chooseMessageFile:cancel') {
              // Aegis打点：用户取消选择企微文件
              this.reportAegis({ action: 'wx_choose_file_cancelled' });
              reject(new Error('用户取消选择'));
            } else {
              // Aegis打点：chooseMessageFile API 调用失败
              this.reportAegis({
                action: 'wx_choose_file_failed',
                error: res.err_msg || '选择文件失败'
              });
              reject(new Error(res.err_msg || '选择文件失败'));
            }
          }
        );
      });
      
      if (!tempFiles || tempFiles.length === 0) return;
      
      // 立即展示"扫描中"弹窗，无需等待文件数据获取
      this.currentUploadedImage = '';
      this.ocrStatus = 'scanning';
      this.scanKey++;
      this.showOcrDialog = true;
      
      // 异步获取文件数据并更新预览
      (async () => {
        try {
          const tempFile = tempFiles[0];
          const base64Data = await this.getLocalFileData(tempFile.localId);
          const fileName = tempFile.name || `file_${Date.now()}`;
          
          let finalBase64 = base64Data;
          if (!base64Data.includes('base64,')) {
            const mimeType = this.getMimeType(fileName);
            finalBase64 = `data:${mimeType};base64,${base64Data}`;
          }
          
          this.currentUploadedImage = finalBase64;
        } catch (err) {
          console.error('获取文件预览数据失败:', err);
        }
      })();
      
      // Aegis打点：文件上传开始（企微文件方式）
      this.reportAegis({
        action: 'image_upload_start',
        uploadMethod: 'wework-file',
        fileCount: tempFiles.length,
        fileName: tempFiles[0] && tempFiles[0].name
      });
      
      this.isUploading = true;
      
      try {
        const uploadedUrls = [];
        
        for (let i = 0; i < tempFiles.length; i++) {
          const tempFile = tempFiles[i];
          
          const base64Data = await this.getLocalFileData(tempFile.localId);
          const fileName = tempFile.name || `file_${Date.now()}_${i}`;
          
          let finalBase64 = base64Data;
          if (!base64Data.includes('base64,')) {
            const mimeType = this.getMimeType(fileName);
            finalBase64 = `data:${mimeType};base64,${base64Data}`;
          }
          
          const file = this.base64ToFile(finalBase64, fileName);
          
          const reqData = new FormData();
          reqData.append('file', file);
          
          const { data } = await request.post(this.action, reqData, {
            headers: {
              'Content-Type': 'multipart/form-data',
              authToken: sessionStorage.getItem('AuthToken'),
              userid: sessionStorage.getItem('userid'),
            },
          });
          
          if (data?.FileList?.length) {
            uploadedUrls.push(...data.FileList.map(item => item.url));
          }
        }
        
        if (uploadedUrls.length > 0) {
          // Aegis打点：文件上传成功（企微文件方式）
          this.reportAegis({
            action: 'image_upload_success',
            uploadMethod: 'wework-file',
            uploadedUrl: uploadedUrls[0],
            fileCount: uploadedUrls.length
          });
          
          this.$emit('upload-complete', uploadedUrls);
          
          // 上传成功后，更新为真实的图片URL并进行OCR识别
          this.currentUploadedImage = uploadedUrls[0];
          
          // 开始OCR识别
          this.performOcr(uploadedUrls[0]);
        } else {
          // Aegis打点：文件上传失败（企微文件方式）
          this.reportAegis({
            action: 'image_upload_failed',
            uploadMethod: 'wework-file',
            reason: 'no_urls_returned'
          });
          
          // 上传失败，关闭弹框
          Toast.fail('上传失败，请重试');
          this.showOcrDialog = false;
        }
      } catch (err) {
        console.error('企微文件上传失败:', err);
        
        // Aegis打点：文件上传异常（企微文件方式）
        this.reportAegis({
          action: 'image_upload_error',
          uploadMethod: 'wework-file',
          error: err.message || String(err)
        });
        
        Toast.fail('上传失败，请重试');
        this.showOcrDialog = false;
      } finally {
        this.isUploading = false;
      }
    },
    
    // 获取微信图片本地数据
    getLocalImageData(localId) {
      return new Promise((resolve, reject) => {
        const wxApi = typeof ww !== 'undefined' ? ww : wx;
        
        wxApi.getLocalImgData({
          localId: localId,
          success: (res) => {
            let localData = res.localData;
            if (localData && !localData.startsWith('data:image')) {
              localData = 'data:image/jpeg;base64,' + localData;
            }
            resolve(localData);
          },
          fail: (err) => {
            // Aegis打点：getLocalImgData 获取图片数据失败
            this.reportAegis({
              action: 'wx_get_local_img_failed',
              error: err?.errMsg || String(err || '')
            });
            reject(err);
          }
        });
      });
    },
    
    // 获取企微文件数据
    getLocalFileData(localId) {
      return new Promise((resolve, reject) => {
        const wwApi = typeof ww !== 'undefined' ? ww : (typeof window.ww !== 'undefined' ? window.ww : null);
        const wxApi = typeof wx !== 'undefined' ? wx : (typeof window.wx !== 'undefined' ? window.wx : null);
        
        if (wwApi && typeof wwApi.getLocalFileData === 'function') {
          wwApi.getLocalFileData({ localId })
            .then((res) => {
              if (res.errMsg === 'getLocalFileData:ok' || res.localData) {
                resolve(res.localData);
              } else {
                // Aegis打点：ww.getLocalFileData 返回异常
                this.reportAegis({
                  action: 'wx_get_local_file_failed',
                  api: 'ww.getLocalFileData',
                  error: res.errMsg || '获取文件数据失败'
                });
                reject(new Error(res.errMsg || '获取文件数据失败'));
              }
            })
            .catch((err) => {
              // Aegis打点：ww.getLocalFileData 调用异常
              this.reportAegis({
                action: 'wx_get_local_file_error',
                api: 'ww.getLocalFileData',
                error: err.errMsg || err.message || '获取文件数据失败'
              });
              reject(new Error(err.errMsg || err.message || '获取文件数据失败'));
            });
          return;
        }
        
        if (wxApi && typeof wxApi.invoke === 'function') {
          wxApi.invoke(
            'getLocalFileData',
            { localId },
            (res) => {
              if (res.err_msg === 'getLocalFileData:ok') {
                resolve(res.localData);
              } else {
                // Aegis打点：wx.invoke getLocalFileData 返回异常
                this.reportAegis({
                  action: 'wx_get_local_file_failed',
                  api: 'wx.invoke.getLocalFileData',
                  error: res.err_msg || '获取文件数据失败'
                });
                reject(new Error(res.err_msg || '获取文件数据失败'));
              }
            }
          );
          return;
        }
        
        // Aegis打点：企微 SDK 未加载
        this.reportAegis({
          action: 'wx_get_local_file_error',
          api: 'none',
          error: '企业微信 SDK 未加载'
        });
        reject(new Error('企业微信 SDK 未加载'));
      });
    },
    
    // base64转File
    base64ToFile(base64Data, filename) {
      const arr = base64Data.split(',');
      const mimeRegex = new RegExp(':(.*?);');
      const mimeMatch = arr[0].match(mimeRegex);
      const mime = (mimeMatch && mimeMatch[1]) ? mimeMatch[1] : 'application/octet-stream';
      const bstr = atob(arr[1]);
      let n = bstr.length;
      const u8arr = new Uint8Array(n);
      while (n--) {
        u8arr[n] = bstr.charCodeAt(n);
      }
      return new File([u8arr], filename, { type: mime });
    },
    
    // 获取MIME类型
    getMimeType(fileName) {
      const parts = fileName.split('.');
      const ext = parts.length > 1 ? parts.pop().toLowerCase() : '';
      const mimeTypes = {
        jpg: 'image/jpeg',
        jpeg: 'image/jpeg',
        png: 'image/png',
        gif: 'image/gif',
        bmp: 'image/bmp',
        webp: 'image/webp',
        svg: 'image/svg+xml',
        default: 'application/octet-stream'
      };
      return mimeTypes[ext] || mimeTypes.default;
    },
    
    // OCR识别（分发到新/旧版实现）
    async performOcr(imageUrl) {
      // Aegis打点：OCR识别开始
      this.reportAegis({
        action: 'ocr_recognition_start',
        imageUrl: imageUrl,
        version: this.useOcrV2 ? 'v2' : 'v1',
        hasCallback: typeof this.ocrCallback === 'function'
      });

      if (this.useOcrV2) {
        return this.performOcrV2(imageUrl);
      }
      return this.performOcrV1(imageUrl);
    },

    // 旧版OCR识别（原逻辑保持不变）
    async performOcrV1(imageUrl) {
      try {
        const response = await ocrImage(imageUrl, this.extraPrompt, {}, store.getters.getConfigItem('ocrFlowKey') || '');
        
        // 检查返回结果
        if (response?.data?.data?.AgentResponse?.Result) {
          const ocrResult = response.data.data.AgentResponse.Result;
          
          // 保存原始识别文本（用于手动模式）- 处理特殊符号周围的空格
          const prereplaceRegConfig = store.getters.getConfigItem('prereplaceReg');
          const prereplaceReg = this.parseRegex(prereplaceRegConfig) || new RegExp('\\s*([-：:])\\s*', 'g');

          this.ocrRawText = ocrResult
            .replace(prereplaceReg, '$1');  // 删除 -、:、： 前后的空格
          
          // 按换行符分割识别结果，并删除每个词中的所有空格
          const splitWordsRegex = new RegExp('[\\n\\s+]');
          let words = this.ocrRawText.split(splitWordsRegex).filter(word => word.trim())
          
          // 获取分割正则表达式（支持字符串格式转换）
          const splitRegConfig = store.getters.getConfigItem('splitReg');
          // 默认正则：匹配冒号（兼容iOS Safari旧版本，不使用后顾断言）
          const splitReg = this.parseRegex(splitRegConfig) || new RegExp('[：:]');

          const replaceRegConfig = store.getters.getConfigItem('replaceReg');
          const replaceReg = this.parseRegex(replaceRegConfig) || new RegExp('\\s+', 'g');
          
          // 进一步按冒号（: 或 ：）分割每个词
          words = words.flatMap(word => {
            // 按中英文冒号分割
            const parts = word.split(splitReg);
            
            // 过滤掉空字符串，并删除每个part中的所有空格、换行符等无意义符号
            const whitespaceRegex = new RegExp('\\s+', 'g');
            const cleanedParts = parts
              .map(part => part.replace(whitespaceRegex, ''))  // 删除所有空格、换行符、制表符等
              .filter(part => part.trim());
            
            // 检测时间格式并合并（如 "12" 和 "30" -> "12:30"）
            // 避免在iOS上使用后顾断言
            const mergedParts = [];
            const digitPattern1 = new RegExp('^\\d{1,2}$');
            const digitPattern2 = new RegExp('^\\d{2}$');
            
            for (let i = 0; i < cleanedParts.length; i++) {
              const current = cleanedParts[i];
              const next = cleanedParts[i + 1];
              
              // 如果当前和下一个都是纯数字，且当前是1-2位数字，下一个是2位数字
              // 则很可能是时间格式，合并它们
              if (
                next && 
                digitPattern1.test(current) && 
                digitPattern2.test(next) &&
                parseInt(current) < 24 &&
                parseInt(next) < 60
              ) {
                mergedParts.push(`${current}:${next}`);
                i++; // 跳过下一个
              } else {
                mergedParts.push(current);
              }
            }
            
            return mergedParts;
          });
          
          // 如果提供了 ocrCallback，则使用回调函数处理识别结果
          if (typeof this.ocrCallback === 'function') {
            try {
              // Aegis打点：开始调用回调处理
              this.reportAegis({
                action: 'ocr_callback_start',
                imageUrl: imageUrl,
                originalWordsCount: words.length
              });
              
              // 调用回调函数，支持同步和异步
              const callbackResult = await this.ocrCallback(ocrResult, words);
              
              // 验证回调返回值必须是数组
              if (Array.isArray(callbackResult)) {
                words = callbackResult.filter(word => word && String(word).trim());
                
                // Aegis打点：回调处理成功
                const countSplitRegex = new RegExp('[\\n\\ ]');
                this.reportAegis({
                  action: 'ocr_callback_success',
                  imageUrl: imageUrl,
                  originalWordsCount: ocrResult.split(countSplitRegex).filter(word => word.trim()).length,
                  processedWordsCount: words.length
                });
              } else {
                console.warn('[ImageUploadInput] ocrCallback 返回值不是数组，使用默认分割结果');
                
                // Aegis打点：回调返回值异常
                this.reportAegis({
                  action: 'ocr_callback_invalid_return',
                  imageUrl: imageUrl,
                  returnType: typeof callbackResult
                });
              }
            } catch (callbackError) {
              console.error('[ImageUploadInput] ocrCallback 执行失败:', callbackError);
              
              // Aegis打点：回调执行异常
              this.reportAegis({
                action: 'ocr_callback_error',
                imageUrl: imageUrl,
                error: callbackError.message || String(callbackError)
              });
              
              // 回调失败时使用默认分割结果
              Toast('处理识别结果失败，使用默认结果');
            }
          }
          
          if (words.length > 0) {
            this.ocrWords = words;
            this.selectedWords = [];
            this._wordSelectionSources = {};
            this.ocrStatus = 'completed'; // 切换到选择阶段
            
            // Aegis打点：OCR识别成功
            this.reportAegis({
              action: 'ocr_recognition_success',
              imageUrl: imageUrl,
              wordsCount: words.length,
              ocrResult: ocrResult.substring(0, 100), // 只记录前100个字符
              usedCallback: typeof this.ocrCallback === 'function'
            });
          } else {
            // Aegis打点：OCR识别失败（无文字内容）
            this.reportAegis({
              action: 'ocr_recognition_failed',
              imageUrl: imageUrl,
              reason: 'no_words_detected'
            });
            
            Toast('未识别到文字内容');
            this.showOcrDialog = false;
          }
        } else {
          // Aegis打点：OCR识别失败（接口返回异常）
          this.reportAegis({
            action: 'ocr_recognition_failed',
            imageUrl: imageUrl,
            reason: 'invalid_response',
            response: JSON.stringify(response?.data?.data?.AgentResponse || {})
          });
          
          Toast('OCR识别失败，请确保图片中包含文字信息');
        console.error('OCR识别失败:', response?.data?.data?.AgentResponse);
          this.showOcrDialog = false;
        }
      } catch (error) {
        console.error('OCR识别失败:', error);
        
        // Aegis打点：OCR识别异常
        this.reportAegis({
          action: 'ocr_recognition_error',
          imageUrl: imageUrl,
          error: error.message || String(error)
        });
        
        Toast('OCR识别失败，请确保图片中包含文字信息');
        this.showOcrDialog = false;
      }
    },

    // 新版OCR识别（含坐标 TextDetections 结构）
    async performOcrV2(imageUrl) {
      try {
        const response = await ocrImage(
          imageUrl,
          this.extraPrompt,
          {},
          this.ocrV2FlowKey,
          { skipGray: true }
        );

        const agentResp = response && response.data && response.data.data && response.data.data.AgentResponse;
        const rawResult = agentResp && agentResp.Result;

        // 新版返回的 Result 可能是对象或 JSON 字符串，统一解析为对象
        let parsed = rawResult;
        if (typeof rawResult === 'string') {
          try {
            parsed = JSON.parse(rawResult);
          } catch (e) {
            parsed = null;
          }
        }

        const detections = parsed
          && parsed.Response
          && Array.isArray(parsed.Response.TextDetections)
          ? parsed.Response.TextDetections
          : (parsed && Array.isArray(parsed.TextDetections) ? parsed.TextDetections : null);

        if (!detections || detections.length === 0) {
          this.reportAegis({
            action: 'ocr_recognition_failed',
            imageUrl: imageUrl,
            version: 'v2',
            reason: 'no_detections'
          });
          Toast('未识别到文字内容');
          this.showOcrDialog = false;
          return;
        }

        // 规范化数据
        const normalized = detections
          .map((d) => ({
            text: (d.DetectedText || '').trim(),
            polygon: Array.isArray(d.Polygon) ? d.Polygon.map(p => ({ x: p.X, y: p.Y })) : [],
            itemPolygon: d.ItemPolygon || null,
            confidence: d.Confidence
          }))
          .filter(item => item.text && item.polygon.length >= 3);

        if (normalized.length === 0) {
          Toast('未识别到有效文字内容');
          this.showOcrDialog = false;
          return;
        }

        // 拼接原始文本（用于手动模式 & 传给 ocrCallback）
        const rawTextJoined = normalized.map(item => item.text).join('\n');
        this.ocrRawText = rawTextJoined;

        // 对每条 detection 应用与 v1 一致的分词规则，得到扁平化的子词列表
        // （去重但保持顺序，用于底部选项区展示）
        const seen = new Set();
        let words = [];
        for (const det of normalized) {
          const { words: subs } = this.splitWordsFromText(det.text);
          for (const w of subs) {
            if (!seen.has(w)) {
              seen.add(w);
              words.push(w);
            }
          }
        }

        // 用户自定义回调（保持与旧版一致的能力）
        if (typeof this.ocrCallback === 'function') {
          try {
            const callbackResult = await this.ocrCallback(rawTextJoined, words);
            if (Array.isArray(callbackResult)) {
              words = callbackResult.filter(w => w && String(w).trim());
            }
          } catch (callbackError) {
            console.error('[ImageUploadInput] ocrCallback 执行失败:', callbackError);
            Toast('处理识别结果失败，使用默认结果');
          }
        }

        this.ocrDetections = normalized;
        this.ocrWords = words;
        this.selectedWords = [];
        this._wordSelectionSources = {};
        this.ocrStatus = 'completed';

        this.reportAegis({
          action: 'ocr_recognition_success',
          version: 'v2',
          imageUrl: imageUrl,
          detectionCount: normalized.length,
          wordsCount: words.length
        });
      } catch (error) {
        console.error('新版OCR识别失败:', error);
        this.reportAegis({
          action: 'ocr_recognition_error',
          version: 'v2',
          imageUrl: imageUrl,
          error: error.message || String(error)
        });
        Toast('OCR识别失败，请确保图片中包含文字信息');
        this.showOcrDialog = false;
      }
    },

    // 图片加载完成，记录自然尺寸和渲染尺寸（用于坐标映射）
    handlePreviewImageLoad(event) {
      const img = event && event.target;
      if (!img) return;
      this.imageNaturalSize = {
        width: img.naturalWidth || 0,
        height: img.naturalHeight || 0
      };
      this.imagePreviewSize = {
        width: img.clientWidth || 0,
        height: img.clientHeight || 0
      };
    },

    /**
     * 根据传入对象计算高亮框在预览区的定位样式。
     * 兼容两种入参：
     *  - 原始 detection：{ polygon: [...] }
     *  - 子高亮框对象：{ box: { leftPct, topPct, widthPct, heightPct } }
     */
    getDetectionBoxStyle(item) {
      if (!item) return { display: 'none' };

      // 纵向向外扩展一定比例，避免 border 遮挡原图文字上下沿
      // 上下各扩 detection 自身高度的 18%（总高度 +36%）
      const expandRatio = 0.18;

      // 优先使用已有的 box（子高亮框场景）
      if (item.box
        && typeof item.box.leftPct === 'number'
        && typeof item.box.widthPct === 'number') {
        const expandH = item.box.heightPct * expandRatio;
        return {
          left: item.box.leftPct + '%',
          top: (item.box.topPct - expandH) + '%',
          width: item.box.widthPct + '%',
          height: (item.box.heightPct + expandH * 2) + '%'
        };
      }

      // 回退到从 polygon 计算
      const bbox = this.getPolygonBoundingBox(item);
      if (!bbox) return { display: 'none' };

      const expandH = bbox.heightPct * expandRatio;
      return {
        left: bbox.leftPct + '%',
        top: (bbox.topPct - expandH) + '%',
        width: bbox.widthPct + '%',
        height: (bbox.heightPct + expandH * 2) + '%'
      };
    },

    // 判断某条检测/子高亮框对应的文本是否处于选中态
    isDetectionSelected(item) {
      if (!item) return false;
      const text = item.text;
      if (!text) return false;
      return this.selectedWords.includes(text);
    },

    // 手动关闭顶部引导提示
    dismissDetectionHint() {
      if (this.hasDismissedDetectionHint) return;
      this.hasDismissedDetectionHint = true;
      this.reportAegis({
        action: 'ocr_detection_hint_dismissed',
        version: 'v2',
        isFullscreen: !!this.fsVisible
      });
    },

    // 点击高亮框（或子高亮框），选中/取消对应词条
    // source: 'detection_box'（弹窗）| 'detection_box_fs'（全屏点击）| 'detection_box_fs_touch'（全屏触摸）
    handleDetectionClick(item, source) {
      if (!item || !item.text) return;
      const resolvedSource = source
        || (this.fsVisible ? 'detection_box_fs' : 'detection_box');
      // 首次通过图上词条框选中，关闭引导提示与呼吸动效
      if (!this.hasClickedDetectionBox) {
        this.hasClickedDetectionBox = true;
      }
      this.toggleWord(item.text, resolvedSource);
      this.reportAegis({
        action: 'ocr_detection_box_clicked',
        version: 'v2',
        source: resolvedSource,
        text: item.text,
        isSubBox: item.isFullBox === false,
        isFullscreen: !!this.fsVisible,
        // 操作后该词是否处于已选中态
        nowSelected: this.selectedWords.includes(item.text)
      });
    },

    /**
     * 通用分词方法（复用 v1 分词规则）：
     * 1. 按换行/空格切成初级 words
     * 2. 按冒号（中/英）再切分为 parts，清理空白
     * 3. 时间格式（例如 12:30）自动合并回单个 part
     * 返回：{ words: string[], meta: { splitReg, replaceReg, prereplaceReg } }
     */
    splitWordsFromText(text) {
      const prereplaceRegConfig = store.getters.getConfigItem('prereplaceReg');
      const prereplaceReg = this.parseRegex(prereplaceRegConfig) || new RegExp('\\s*([-：:])\\s*', 'g');

      const cleanedText = String(text || '').replace(prereplaceReg, '$1');

      const splitWordsRegex = new RegExp('[\\n\\s+]');
      const initialWords = cleanedText.split(splitWordsRegex).filter(w => w.trim());

      const splitRegConfig = store.getters.getConfigItem('splitReg');
      const splitReg = this.parseRegex(splitRegConfig) || new RegExp('[：:]');

      const digitPattern1 = new RegExp('^\\d{1,2}$');
      const digitPattern2 = new RegExp('^\\d{2}$');
      const whitespaceRegex = new RegExp('\\s+', 'g');

      const words = initialWords.flatMap((word) => {
        const parts = word.split(splitReg);
        const cleanedParts = parts
          .map(p => p.replace(whitespaceRegex, ''))
          .filter(p => p.trim());

        const mergedParts = [];
        for (let i = 0; i < cleanedParts.length; i++) {
          const current = cleanedParts[i];
          const next = cleanedParts[i + 1];
          if (
            next
            && digitPattern1.test(current)
            && digitPattern2.test(next)
            && parseInt(current) < 24
            && parseInt(next) < 60
          ) {
            mergedParts.push(`${current}:${next}`);
            i++;
          } else {
            mergedParts.push(current);
          }
        }
        return mergedParts;
      });

      return { words, cleanedText };
    },

    /**
     * 对单条 detection 按 splitWordsFromText 的规则进行拆分，
     * 并根据每个子词在原文本中的字符宽度占比，把 polygon 外接矩形
     * 沿水平方向切分成多个「子高亮框」。
     *
     * 简化假设：
     * - detection.text 通常是一行文本，水平书写。
     * - 每个字符视觉宽度近似相同（中英混排会有偏差，但可接受）。
     * - 若拆分结果为 1 个词，则整框直接返回。
     *
     * 返回：[{ text, detection, box: { leftPct, topPct, widthPct, heightPct } }]
     * 其中 box 是相对图片自然尺寸的百分比。
     */
    buildSubDetectionsFor(detection) {
      if (!detection || !detection.text) return [];

      const originalText = detection.text;
      const { words: subWords } = this.splitWordsFromText(originalText);

      // 无法拆分或只有一个子词 → 使用整框
      if (subWords.length === 0) return [];

      const baseBox = this.getPolygonBoundingBox(detection);
      if (!baseBox) return [];

      if (subWords.length === 1) {
        return [{
          text: subWords[0],
          detection,
          box: baseBox,
          isFullBox: true
        }];
      }

      // 多子词：按每个子词在原文本中的字符长度占比切分横向区间
      // 用 originalText 的 "可视字符宽度" 估算：中文/日韩全宽字符 ≈ 2，其他 ≈ 1
      const measureWidth = (str) => {
        let w = 0;
        for (const ch of String(str)) {
          const code = ch.codePointAt(0) || 0;
          const isWide = code > 0x2E80 && !(code >= 0xFF61 && code <= 0xFF9F); // 简化：CJK 类
          w += isWide ? 2 : 1;
        }
        return w || 1;
      };

      // 逐个子词在原文本中定位其起始下标（顺序查找，允许中间是分隔符/空白）
      let cursor = 0;
      const spans = [];
      for (const w of subWords) {
        const idx = originalText.indexOf(w, cursor);
        if (idx === -1) {
          // 兜底：找不到就按顺序均分（下面统一处理）
          spans.push(null);
        } else {
          spans.push({ start: idx, end: idx + w.length, text: w });
          cursor = idx + w.length;
        }
      }

      // 若有任意一个子词未找到，退化为按字符长度均分整个横向区间
      const allFound = spans.every(s => s !== null);
      const totalWidth = measureWidth(originalText);

      const results = [];
      if (allFound) {
        // 关键优化：分隔符/空白宽度平均分给两侧子词
        // 子框区间 = [上一词末尾中点, 本词末尾中点]，两端分别顶到 0 和 totalWidth
        // 这样所有子框首尾相连，覆盖整个外接矩形，视觉不再"偏小/偏窄"
        const midPoints = [0];
        for (let i = 0; i < spans.length - 1; i++) {
          const currEnd = measureWidth(originalText.slice(0, spans[i].end));
          const nextStart = measureWidth(originalText.slice(0, spans[i + 1].start));
          midPoints.push((currEnd + nextStart) / 2);
        }
        midPoints.push(totalWidth);

        for (let i = 0; i < spans.length; i++) {
          const startW = midPoints[i];
          const endW = midPoints[i + 1];
          const startPct = startW / totalWidth;
          const widthRatio = (endW - startW) / totalWidth;
          results.push({
            text: spans[i].text,
            detection,
            box: {
              leftPct: baseBox.leftPct + baseBox.widthPct * startPct,
              topPct: baseBox.topPct,
              widthPct: baseBox.widthPct * widthRatio,
              heightPct: baseBox.heightPct
            },
            isFullBox: false
          });
        }
      } else {
        // 均分兜底
        const weights = subWords.map(w => measureWidth(w));
        const weightSum = weights.reduce((a, b) => a + b, 0) || 1;
        let acc = 0;
        for (let i = 0; i < subWords.length; i++) {
          const ratio = weights[i] / weightSum;
          results.push({
            text: subWords[i],
            detection,
            box: {
              leftPct: baseBox.leftPct + baseBox.widthPct * acc,
              topPct: baseBox.topPct,
              widthPct: baseBox.widthPct * ratio,
              heightPct: baseBox.heightPct
            },
            isFullBox: false
          });
          acc += ratio;
        }
      }

      return results;
    },

    // 提取 polygon 外接矩形（返回百分比，相对图片自然尺寸）
    getPolygonBoundingBox(detection) {
      if (!detection || !detection.polygon || detection.polygon.length === 0) return null;
      const { width: nw, height: nh } = this.imageNaturalSize;
      if (!nw || !nh) return null;

      let minX = Infinity;
      let minY = Infinity;
      let maxX = -Infinity;
      let maxY = -Infinity;
      detection.polygon.forEach((p) => {
        if (p.x < minX) minX = p.x;
        if (p.y < minY) minY = p.y;
        if (p.x > maxX) maxX = p.x;
        if (p.y > maxY) maxY = p.y;
      });

      return {
        leftPct: (minX / nw) * 100,
        topPct: (minY / nh) * 100,
        widthPct: ((maxX - minX) / nw) * 100,
        heightPct: ((maxY - minY) / nh) * 100
      };
    },

    // 图片被点击（新版启用时打开全屏放大预览；旧版走 ImagePreview）
    handlePreviewImageClick(event) {
      if (this.useOcrV2 && this.ocrDetections.length > 0) {
        this.openFullscreenPreview();
      } else {
        this.previewImage(event);
      }
    },

    openFullscreenPreview() {
      this.fsResetTransform();
      this.showFullscreenPreview = true;
      // 将全屏层挂到 body，脱离外层 dialog/popup 的 stacking context，避免被遮挡
      this.$nextTick(() => {
        const el = this.$refs.fullscreenPreview;
        if (el && el.parentNode !== document.body) {
          document.body.appendChild(el);
        }
      });
      this.reportAegis({
        action: 'ocr_fullscreen_open',
        version: 'v2',
        detectionCount: this.ocrDetections.length
      });
    },

    closeFullscreenPreview() {
      // 节点虽被 teleport 到 body，但 Vue 通过 v-if=false 仍会调用 parentNode.removeChild
      // 自动完成销毁（含 transition leave 动画），无需手动移除
      this.showFullscreenPreview = false;
      this.reportAegis({
        action: 'ocr_fullscreen_close',
        version: 'v2'
      });
    },

    // 移除已被 append 到 body 的全屏预览节点（组件销毁时兜底）
    removeFullscreenPreviewFromBody() {
      const el = this.$refs.fullscreenPreview;
      if (el && el.parentNode === document.body) {
        document.body.removeChild(el);
      }
    },

    handleFullscreenImageLoad() {
      // 图片进入放大层加载完成，重置一次 transform 使初始处于居中 1x
      this.fsResetTransform();
    },

    fsResetTransform() {
      this.fsScale = 1;
      this.fsTranslate = { x: 0, y: 0 };
    },

    fsZoomIn() {
      this.fsSetScale(Math.min(this.fsScale + 0.3, 5));
    },

    fsZoomOut() {
      const next = Math.max(this.fsScale - 0.3, 1);
      this.fsSetScale(next);
      if (next === 1) {
        this.fsTranslate = { x: 0, y: 0 };
      }
    },

    fsSetScale(nextScale) {
      // 缩放变化时，等比例收缩位移，避免图跳出可视区
      const ratio = nextScale / this.fsScale;
      this.fsTranslate = {
        x: this.fsTranslate.x * ratio,
        y: this.fsTranslate.y * ratio
      };
      this.fsScale = nextScale;
    },

    onFsWheel(event) {
      // 桌面端滚轮缩放
      const delta = event.deltaY < 0 ? 0.15 : -0.15;
      const next = Math.min(Math.max(this.fsScale + delta, 1), 5);
      this.fsSetScale(next);
      if (next === 1) this.fsTranslate = { x: 0, y: 0 };
    },

    onFsTouchStart(event) {
      const touches = event.touches;
      this.fsGesture.moved = false;
      this.fsGesture.touchStartAt = Date.now();

      if (touches.length === 2) {
        // 双指缩放
        this.fsGesture.isPinching = true;
        this.fsGesture.isDragging = false;
        this.fsGesture.pinchStartDist = this._fsDistance(touches[0], touches[1]);
        this.fsGesture.pinchStartScale = this.fsScale;
      } else if (touches.length === 1) {
        // 单指拖拽（仅在放大后启用）
        if (this.fsScale > 1.001) {
          this.fsGesture.isDragging = true;
          this.fsGesture.startX = touches[0].clientX;
          this.fsGesture.startY = touches[0].clientY;
          this.fsGesture.startTranslateX = this.fsTranslate.x;
          this.fsGesture.startTranslateY = this.fsTranslate.y;
        }
      }
    },

    onFsTouchMove(event) {
      const touches = event.touches;

      if (this.fsGesture.isPinching && touches.length === 2) {
        event.preventDefault();
        const dist = this._fsDistance(touches[0], touches[1]);
        if (this.fsGesture.pinchStartDist > 0) {
          const nextScale = Math.min(
            Math.max(this.fsGesture.pinchStartScale * (dist / this.fsGesture.pinchStartDist), 1),
            5
          );
          this.fsScale = nextScale;
          this.fsGesture.moved = true;
        }
        return;
      }

      if (this.fsGesture.isDragging && touches.length === 1) {
        event.preventDefault();
        const dx = touches[0].clientX - this.fsGesture.startX;
        const dy = touches[0].clientY - this.fsGesture.startY;
        if (Math.abs(dx) > 3 || Math.abs(dy) > 3) {
          this.fsGesture.moved = true;
        }
        this.fsTranslate = {
          x: this.fsGesture.startTranslateX + dx,
          y: this.fsGesture.startTranslateY + dy
        };
      }
    },

    onFsTouchEnd() {
      // 结束手势；缩放收敛到 [1, 5]
      if (this.fsScale < 1) {
        this.fsScale = 1;
        this.fsTranslate = { x: 0, y: 0 };
      }
      this.fsGesture.isDragging = false;
      this.fsGesture.isPinching = false;
    },

    // 高亮框 touchend：只有在"没有明显移动"时视为点击
    onFsBoxTouchEnd(event, item) {
      if (this.fsGesture.moved) return;
      const elapsed = Date.now() - this.fsGesture.touchStartAt;
      if (elapsed > 500) return;
      event.preventDefault();
      this.handleDetectionClick(item, 'detection_box_fs_touch');
    },

    _fsDistance(t1, t2) {
      const dx = t1.clientX - t2.clientX;
      const dy = t1.clientY - t2.clientY;
      return Math.sqrt(dx * dx + dy * dy);
    },

    /**
     * 切换选中文字
     * @param {string} word 待切换的词
     * @param {string} source 选词入口，用于 Aegis 分析用户选词偏好：
     *   - 'word_chip'         词条列表 chip（弹窗内文字列表）
     *   - 'detection_box'     弹窗内图片高亮框
     *   - 'detection_box_fs'  全屏预览图片高亮框（点击）
     *   - 'detection_box_fs_touch'  全屏预览图片高亮框（触摸）
     *   - 'unknown'           未指定（兜底）
     */
    toggleWord(word, source = 'unknown') {
      // 该词是否属于图片高亮框覆盖到的子词，用于分析"用户是否直接在图片上就能找到该词"
      const inDetection = Array.isArray(this.ocrSubDetections)
        && this.ocrSubDetections.some(s => s && s.text === word);

      const commonPayload = {
        word,
        source,
        ocrMode: this.ocrMode,
        ocrVersion: this.useOcrV2 ? 'v2' : 'v1',
        isFullscreen: !!this.fsVisible,
        wordInDetection: inDetection,
        totalWordsCount: this.ocrWords.length
      };

      const index = this.selectedWords.indexOf(word);
      if (index > -1) {
        this.selectedWords.splice(index, 1);

        // 记录该词最近一次选中方式的映射，用于 confirm 时聚合来源分布
        if (this._wordSelectionSources) {
          delete this._wordSelectionSources[word];
        }

        // Aegis打点：取消选择文字
        this.reportAegis({
          action: 'ocr_word_unselected',
          ...commonPayload,
          currentSelectionCount: this.selectedWords.length
        });
      } else {
        this.selectedWords.push(word);

        // 记录该词的选中来源
        if (!this._wordSelectionSources) this._wordSelectionSources = {};
        this._wordSelectionSources[word] = source;

        // Aegis打点：选择文字
        this.reportAegis({
          action: 'ocr_word_selected',
          ...commonPayload,
          currentSelectionCount: this.selectedWords.length
        });
      }
    },
    
    // 确认选择
    confirmSelection() {
      if (this.selectedWords.length > 0) {
        const selectedText = this.selectedWords.join(' ');

        // 聚合每个已选词的选中来源，用于分析用户选词偏好
        const sourceMap = this._wordSelectionSources || {};
        const sourceCounts = {};
        const wordSources = [];
        for (const w of this.selectedWords) {
          const s = sourceMap[w] || 'unknown';
          sourceCounts[s] = (sourceCounts[s] || 0) + 1;
          wordSources.push({ word: w, source: s });
        }
        // 判定主要选词方式（占比最高的来源）
        let primarySource = 'unknown';
        let maxCount = 0;
        for (const k of Object.keys(sourceCounts)) {
          if (sourceCounts[k] > maxCount) {
            maxCount = sourceCounts[k];
            primarySource = k;
          }
        }

        // Aegis打点：用户确认选择
        this.reportAegis({
          action: 'ocr_selection_confirmed',
          selectedWordsCount: this.selectedWords.length,
          selectedText: selectedText.substring(0, 100), // 只记录前100个字符
          selectedWords: this.selectedWords.slice(0, 20), // 记录用户选中的具体词（最多20个）
          totalWordsCount: this.ocrWords.length,
          hasSwitchedToManual: this.hasSwitchedToManual,
          hasCopiedManually: this.hasCopiedManually,
          // 选词方式相关
          ocrVersion: this.useOcrV2 ? 'v2' : 'v1',
          primarySelectionSource: primarySource,
          selectionSourceCounts: sourceCounts,
          wordSources: wordSources.slice(0, 20)
        });

        // 清理来源映射，避免影响下一次 OCR
        this._wordSelectionSources = {};

        this.$emit('ocr-text-selected', selectedText);
        this.showOcrDialog = false;
      }
    },
    
    // 复制文本到剪贴板（手动模式）
    async copyToClipboard() {
      try {
        // 使用现代剪贴板 API
        if (navigator.clipboard && window.isSecureContext) {
          await navigator.clipboard.writeText(this.ocrRawText);
        } else {
          // 降级方案：使用 textarea
          const textarea = document.createElement('textarea');
          textarea.value = this.ocrRawText;
          textarea.style.position = 'fixed';
          textarea.style.opacity = '0';
          document.body.appendChild(textarea);
          textarea.select();
          document.execCommand('copy');
          document.body.removeChild(textarea);
        }
        
        Toast.success('复制成功');
        
        // 记录手动复制行为
        this.hasCopiedManually = true;
        
        // Aegis打点：复制文本成功
        this.reportAegis({
          action: 'ocr_text_copied',
          mode: 'manual',
          textLength: this.ocrRawText.length,
          copiedText: this.ocrRawText.substring(0, 200) // 记录复制的文本（最多200字符）
        });
      } catch (error) {
        console.error('复制失败:', error);
        Toast.fail('复制失败，请手动复制');
        
        // Aegis打点：复制文本失败
        this.reportAegis({
          action: 'ocr_text_copy_failed',
          error: error.message || String(error)
        });
      }
    },
    
    // 监听用户在"原始文本"区域手动选中复制（非"复制全部"按钮）
    handleManualCopy(event) {
      const selection = window.getSelection();
      const copiedText = selection ? selection.toString() : '';
      if (copiedText) {
        this.hasCopiedManually = true;
        
        // Aegis打点：用户手动选中复制
        this.reportAegis({
          action: 'ocr_text_manual_copy',
          mode: 'manual',
          copiedText: copiedText.substring(0, 200),
          copiedTextLength: copiedText.length,
          isFullText: copiedText.trim() === this.ocrRawText.trim()
        });
      }
    },
    
    // 预览图片
    previewImage() {
      // Aegis打点：用户点击预览图片
      this.reportAegis({
        action: 'ocr_image_preview',
        imageUrl: this.currentUploadedImage
      });
      
      ImagePreview({
        images: [this.currentUploadedImage],
        startPosition: 0,
        closeable: true
      });
    },
    
    // 重新拍摄
    async retakePhoto() {
      // Aegis打点：用户点击重新拍摄
      this.reportAegis({
        action: 'ocr_retake_photo',
        previousImageUrl: this.currentUploadedImage
      });
      
      // useNewOCR 启用时，关闭弹窗后重新走企微API选择
      if (this.shouldUseWechatApi) {
        this.showOcrDialog = false;
        await this.$nextTick();
        await this.handleWechatUpload();
        return;
      }
      
      // 未启用 useNewOCR 时，触发 uploader 原生点击
      if (this.$refs.uploader && this.$refs.uploader.$el) {
        const input = this.$refs.uploader.$el.querySelector('input[type="file"]');
        if (input) {
          input.click();
        }
      }
    },
    
    // 对话框关闭时清空数据
    handleOcrDialogClosed() {
      // 如果弹框已经被重新打开（新一轮识别），跳过清理，避免覆盖新数据
      if (this.showOcrDialog) return;
      
      // Aegis打点：用户关闭弹窗
      // 判断本轮中是否发生过"点击图片区域选中"的行为（无论最终是否保留该词）
      const sourceMap = this._wordSelectionSources || {};
      const imageSources = ['detection_box', 'detection_box_fs', 'detection_box_fs_touch'];
      const hasPickedFromImage = Object.keys(sourceMap).some(w => imageSources.includes(sourceMap[w]));
      // 当前仍处于已选中态的词里，有多少是通过点击图片区域选中的
      const currentImagePickedCount = this.selectedWords.filter(w => imageSources.includes(sourceMap[w])).length;

      this.reportAegis({
        action: 'ocr_dialog_closed',
        closedAt: this.ocrStatus, // scanning 或 completed
        hadSelectedWords: this.selectedWords.length > 0,
        selectedWordsCount: this.selectedWords.length,
        selectedWords: this.selectedWords.slice(0, 20), // 记录选中的具体词
        totalWordsCount: this.ocrWords.length,
        hasSwitchedToManual: this.hasSwitchedToManual,
        hasCopiedManually: this.hasCopiedManually,
        currentMode: this.ocrMode,
        // 是否使用过"点击图片区域选中"这一交互（含全屏点击/触摸）
        hasPickedFromImage,
        currentImagePickedCount
      });
      
      this.ocrWords = [];
      this.selectedWords = [];
      this._wordSelectionSources = {};
      this.ocrStatus = 'scanning';
      this.hasSwitchedToManual = false;
      this.hasCopiedManually = false;
      this.ocrDetections = [];
      this.hasClickedDetectionBox = false;
      this.hasDismissedDetectionHint = false;
      this.imageNaturalSize = { width: 0, height: 0 };
      this.imagePreviewSize = { width: 0, height: 0 };
      this.showFullscreenPreview = false;
      this.fsResetTransform();

      // 释放本地 blob URL
      if (this.currentUploadedImage && this.currentUploadedImage.startsWith('blob:')) {
        URL.revokeObjectURL(this.currentUploadedImage);
      }
      
      this.currentUploadedImage = '';
      this.pendingImageFile = null;
    },
    
    // Aegis打点上报封装方法
    reportAegis(data) {
      try {
        const { aegis } = this.$store.state.user;
        if (aegis) {
          // 获取上传的原始图片链接（排除本地blob URL）
          const imageUrl = this.currentUploadedImage && !this.currentUploadedImage.startsWith('blob:') && !this.currentUploadedImage.startsWith('data:')
            ? this.currentUploadedImage
            : '';
          aegis.infoAll({
            msg: JSON.stringify({
              ...data,
              component: 'ImageUploadInput',
              fieldName: this.fieldName,
              fieldTitle: this.fieldTitle,
              processDefinitionKey: this.processDefinitionKey || '',
              uploadedImageUrl: imageUrl,
              timestamp: new Date().toISOString()
            }),
            ext1: 'image-upload-ocr',
            trace: `ocr-${data.action}`,
          });
        }
      } catch (reportError) {
        console.error('[ImageUploadInput] Aegis上报失败:', reportError);
      }
    }
  }
};
</script>

<style scoped>
.image-upload-input {
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.image-upload-input >>> .van-uploader {
  display: inline-flex;
}

.image-upload-input >>> .van-uploader__wrapper {
  display: inline-flex;
}

.upload-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  align-self: center;
  cursor: pointer;
  color: #1989fa;
  padding: 0;
  margin-right: 2px;
  position: relative;
  width: 28px;
  height: 28px;
  flex-shrink: 0;
}

.upload-icon:active {
  opacity: 0.7;
}

/* 工具栏样式 */
.toolbar-button {
  display: flex;
  align-items: center;
  gap: 5px;
  cursor: pointer;
  padding: 5px 10px;
  border-radius: 16px;
  background: #f5f6f8;
  transition: background 0.2s;
  color: #0052D9;
  user-select: none;
}

.toolbar-button:hover {
  background: #e8f0fe;
}

.toolbar-button--loading {
  cursor: not-allowed;
  opacity: 0.7;
}

.toolbar-button__text {
  font-size: 13px;
  white-space: nowrap;
}

.toolbar-button svg {
  flex-shrink: 0;
}

.loading-spinner {
  animation: spin 1s linear infinite;
  color: #1989fa;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

/* 触发按钮包裹层 */
.upload-trigger-wrapper {
  position: relative;
  display: inline-flex;
  align-items: center;
}

/* 首次使用气泡提示 */
.ocr-onboarding-tip {
  position: absolute;
  bottom: calc(100% + 8px);
  left: 50%;
  transform: translateX(-50%);
  background: rgba(0, 0, 0, 0.78);
  color: #fff;
  font-size: 12px;
  line-height: 1.5;
  padding: 8px 12px;
  border-radius: 6px;
  display: flex;
  width: 100px;
  word-break: break-all;
  align-items: center;
  gap: 6px;
  white-space: normal;
  pointer-events: auto;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  z-index: 10000;
  cursor: pointer;
}

.ocr-onboarding-tip::after {
  content: '';
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  border: 6px solid transparent;
  border-top-color: rgba(0, 0, 0, 0.78);
}

.ocr-onboarding-tip__close {
  font-size: 14px;
  opacity: 0.7;
  margin-left: 2px;
  line-height: 1;
}

.ocr-onboarding-tip__close:active {
  opacity: 1;
}

/* 气泡提示过渡动画 */
.ocr-tip-fade-enter-active {
  animation: ocrTipFadeIn 0.3s ease;
}
.ocr-tip-fade-leave-active {
  animation: ocrTipFadeIn 0.3s ease reverse;
}
@keyframes ocrTipFadeIn {
  from { opacity: 0; transform: translateX(-50%) translateY(4px); }
  to { opacity: 1; transform: translateX(-50%) translateY(0); }
}

/* OCR对话框通用样式 */
.ocr-dialog {
  padding: 20px;
  overflow-x: hidden;
}

/* 扫描阶段样式 */
.ocr-scanning {
  padding-bottom: 10px;
}

.ocr-scanning__header {
  margin-bottom: 20px;
  text-align: center;
}

.ocr-scanning__header h3 {
  margin: 0 0 8px 0;
  font-size: 18px;
  font-weight: 500;
  color: #323233;
}

.ocr-scanning__tip {
  margin: 0;
  font-size: 14px;
  color: #969799;
}

.ocr-scanning__image-container {
  position: relative;
  width: 100%;
  max-width: 400px;
  margin: 0 auto;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
}

.ocr-scanning__image {
  width: 100%;
  height: auto;
  display: block;
}

.ocr-scanning__placeholder {
  width: 100%;
  height: 200px;
  background: #f5f5f5;
  border-radius: 8px;
}

/* 扫描线动画 */
.scan-line {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 3px;
  background: linear-gradient(90deg, 
    transparent 0%, 
    #1989fa 50%, 
    transparent 100%
  );
  box-shadow: 0 0 10px #1989fa, 0 0 20px #1989fa;
  animation: scanning 2s linear infinite;
  z-index: 2;
}

@keyframes scanning {
  0% {
    top: 0;
    opacity: 0;
  }
  10% {
    opacity: 1;
  }
  90% {
    opacity: 1;
  }
  100% {
    top: 100%;
    opacity: 0;
  }
}

/* 扫描遮罩效果 */
.scan-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    180deg,
    rgba(25, 137, 250, 0.1) 0%,
    transparent 30%,
    transparent 70%,
    rgba(25, 137, 250, 0.1) 100%
  );
  pointer-events: none;
  z-index: 1;
  animation: pulse 2s ease-in-out infinite;
}

@keyframes pulse {
  0%, 100% {
    opacity: 0.3;
  }
  50% {
    opacity: 0.6;
  }
}

/* 选择阶段样式 */
.ocr-selection {
  display: flex;
  flex-direction: column;
  height: 100%;
  max-height: 85vh;
  min-height: 60vh;
}

/* 标题区域固定在顶部 */
.ocr-dialog__header {
  flex-shrink: 0;
  padding: 0 12px 6px 0;
  background: #fff;
  position: sticky;
  top: 0;
  z-index: 10;
  margin-right: 40px;


}

.ocr-dialog__title-row {
  padding-right: 40px;  /* 为右上角关闭按钮预留空间 */
  margin-bottom: 12px;
}

.ocr-dialog__header h3 {
  margin: 0 0 4px 0;
  font-size: 18px;
  font-weight: 600;
  color: #323233;
}

.ocr-dialog__tip {
  margin: 0;
  font-size: 13px;
  color: #969799;
  line-height: 1.5;
}

/* Tabs 标签页样式 */
.ocr-mode-tabs {
  margin: 0 -16px;
}

.ocr-mode-tabs .van-tabs__wrap {
  border-bottom: 1px solid #ebedf0;
}

.ocr-mode-tabs .van-tabs__line {
  background-color: #1989fa;
  height: 3px;
}

.tab-title {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 14px;
}

.tab-title .van-icon {
  font-size: 16px;
}

.ocr-selection__scrollable {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding-top: 12px;
  -webkit-overflow-scrolling: touch;
  /* 细化滚动条，避免占用视觉空间 */
  scrollbar-width: thin;
  scrollbar-color: rgba(0, 0, 0, 0.18) transparent;
}

.ocr-selection__scrollable::-webkit-scrollbar {
  width: 4px;
}

.ocr-selection__scrollable::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.18);
  border-radius: 2px;
}

.ocr-selection__scrollable::-webkit-scrollbar-track {
  background: transparent;
}

/* 原图片预览区域 */
.ocr-selection__image-preview {
  position: relative;
  margin-bottom: 3px;
  border-radius: 8px;
  overflow: hidden;
  max-height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f7f8fa;
  cursor: pointer;
  transition: all 0.3s;
  width: 100%;
}

.ocr-selection__image-preview:hover {
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.15);
}

.ocr-selection__image-preview:active {
  transform: scale(0.98);
}

/* 重新拍摄按钮 - 移动端优化（紧凑版） */
.retake-photo-btn {
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 5;
  background: rgba(255, 255, 255, 0.95);
  -webkit-backdrop-filter: blur(4px);
  backdrop-filter: blur(4px);
  border: 1px solid #ebedf0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  font-size: 10px;
  padding: 4px 8px;
  height: auto;
  min-height: 20px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  gap: 4px;
  transition: all 0.2s ease;
}

/* 移动端点击态 */
.retake-photo-btn:active {
  transform: scale(0.92);
  background: rgba(245, 245, 245, 0.95);
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.15);
}

.retake-photo-btn .van-icon {
  font-size: 10px;
  color: #323233;
}

.preview-image {
  max-width: 100%;
  max-height: 200px;
  height: auto;
  display: block;
  object-fit: contain;
}

/* 新版OCR：坐标高亮框叠加层 */
.preview-image-wrapper {
  position: relative;
  display: inline-block;
  line-height: 0;
  max-width: 100%;
  max-height: 100%;
}

/* 新版预览：随图片自适应尺寸，同时限制最大高度避免撑爆弹窗 */
.ocr-selection__image-preview--v2 {
  /* 覆盖旧版 200px；改为按图片比例自适应，并有最大高度上限 */
  max-height: 50vh;
  min-height: 0;
  height: auto;
  cursor: pointer;
  background: #f2f3f5;
  /* 关键：不在此层滚动，避免与外层 .ocr-selection__scrollable 产生嵌套滚动条 */
  overflow: hidden;
  /* 让内容在容器内居中显示 */
  align-items: center;
  justify-content: center;
}

.ocr-selection__image-preview--v2:hover {
  box-shadow: none;
}

.ocr-selection__image-preview--v2:active {
  transform: none;
}

.ocr-selection__image-preview--v2 .preview-image-wrapper {
  /* 让 wrapper 保持图片比例，宽高交给内部 img 决定 */
  max-width: 100%;
  max-height: 50vh;
  display: inline-block;
  line-height: 0;
}

.ocr-selection__image-preview--v2 .preview-image {
  /* 新版下不再受 200px 限制：按容器宽高中较严格的一边收敛，保持比例 */
  display: block;
  max-width: 100%;
  max-height: 50vh;
  width: auto;
  height: auto;
  object-fit: contain;
  margin: 0 auto;
}

.ocr-detection-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 3;
}

.ocr-detection-box {
  position: absolute;
  border: 2px solid rgba(25, 137, 250, 0.95);
  background: rgba(25, 137, 250, 0.18);
  border-radius: 3px;
  cursor: pointer;
  pointer-events: auto;
  box-sizing: border-box;
  transition: background 0.15s, border-color 0.15s, box-shadow 0.15s;
  overflow: visible;
}

/* 未点击过任何词条框时，蓝框做呼吸脉冲动效，增强"可点击"辨识度（播放 3 次后自动停止） */
.ocr-detection-overlay--pulse .ocr-detection-box:not(.ocr-detection-box--active) {
  animation: ocrDetectionPulse 1.6s ease-in-out 3;
}

@keyframes ocrDetectionPulse {
  0%, 100% {
    box-shadow: 0 0 0 0 rgba(25, 137, 250, 0);
    background: rgba(25, 137, 250, 0.18);
  }
  50% {
    box-shadow: 0 0 0 3px rgba(25, 137, 250, 0.35);
    background: rgba(25, 137, 250, 0.32);
  }
}

.ocr-detection-box:hover {
  background: rgba(64, 169, 255, 0.18);
  box-shadow: 0 0 0 1px rgba(64, 169, 255, 0.5);
}

.ocr-detection-box--active {
  border-color: #ff8f1f;
  background: rgba(255, 143, 31, 0.24);
  box-shadow: 0 0 0 1px rgba(255, 143, 31, 0.5);
}

.ocr-detection-box__label {
  position: absolute;
  bottom: 100%;
  left: 0;
  padding: 1px 4px;
  font-size: 10px;
  line-height: 1.2;
  color: #fff;
  background: rgba(0, 0, 0, 0.65);
  border-radius: 2px;
  white-space: nowrap;
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  pointer-events: none;
  transform: translateY(-2px);
  opacity: 0;
  transition: opacity 0.15s;
}

.ocr-detection-box--active .ocr-detection-box__label {
  opacity: 1;
}

/* 使用 hover:none 判定，避免移动端点击后 hover 粘滞导致气泡无法及时消失 */
@media (hover: hover) {
  .ocr-detection-box:hover .ocr-detection-box__label {
    opacity: 1;
  }
}

.ocr-detection-hint-fade-enter-active,
.ocr-detection-hint-fade-leave-active {
  transition: opacity 0.25s ease;
}

.ocr-detection-hint-fade-enter,
.ocr-detection-hint-fade-leave-to {
  opacity: 0;
}

/* 行动化引导提示（与默认 tip 保持完全一致的排版尺寸，仅改颜色/加图标以引起注意） */
.ocr-dialog__tip--action {
  align-items: center;
  gap: 4px;
  margin: 0;
  padding: 0;
  font-size: 13px;
  line-height: 1.5;
  color: #1989fa;
  background: transparent;
  border: none;
  cursor: pointer;
}

.ocr-dialog__tip-icon {
  font-size: 14px;
}

/* 图片右上角放大提示图标 */
.ocr-zoom-hint {
  position: absolute;
  top: 8px;
  left: 8px;
  width: 26px;
  height: 26px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  background: rgba(0, 0, 0, 0.5);
  border-radius: 50%;
  font-size: 14px;
  pointer-events: none;
  z-index: 4;
  -webkit-backdrop-filter: blur(3px);
  backdrop-filter: blur(3px);
}

/* ============ 全屏放大预览 ============ */
.ocr-fullscreen-preview {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 3000;
  background: rgba(0, 0, 0, 0.92);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  touch-action: none;
  user-select: none;
  -webkit-user-select: none;
}

.ocr-fullscreen-fade-enter-active,
.ocr-fullscreen-fade-leave-active {
  transition: opacity 0.2s ease;
}
.ocr-fullscreen-fade-enter,
.ocr-fullscreen-fade-leave-to {
  opacity: 0;
}

.ocr-fullscreen-preview__toolbar {
  position: relative;
  flex-shrink: 0;
  padding: 12px 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: #fff;
  background: linear-gradient(to bottom, rgba(0, 0, 0, 0.55), transparent);
  z-index: 2;
}

.ocr-fullscreen-preview__count {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.9);
}

.ocr-fullscreen-preview__close {
  font-size: 22px;
  color: #fff;
  padding: 4px;
  cursor: pointer;
}

.ocr-fullscreen-preview__stage {
  flex: 1;
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
}

.ocr-fullscreen-preview__transform {
  will-change: transform;
  display: inline-block;
  max-width: 100%;
  max-height: 100%;
}

.ocr-fullscreen-preview__image-wrap {
  position: relative;
  display: inline-block;
  line-height: 0;
}

.ocr-fullscreen-preview__image {
  display: block;
  max-width: 92vw;
  max-height: 72vh;
  width: auto;
  height: auto;
  -webkit-user-drag: none;
  user-select: none;
  pointer-events: none;
}

/* 全屏叠加层的高亮框：更清晰，标签常显 */
.ocr-detection-overlay--fs {
  z-index: 3;
}

.ocr-detection-box--fs {
  border-width: 2px;
  border-color: rgba(64, 169, 255, 0.95);
  background: rgba(64, 169, 255, 0.14);
}

.ocr-detection-box--fs:hover {
  background: rgba(64, 169, 255, 0.2);
}

.ocr-detection-box--fs.ocr-detection-box--active {
  border-color: #ff8f1f;
  background: rgba(255, 143, 31, 0.28);
  box-shadow: 0 0 0 2px rgba(255, 143, 31, 0.5);
}

/* 全屏 label 与弹窗内一致：默认隐藏，仅选中/hover 时显示 */
.ocr-detection-box__label--fs {
  font-size: 11px;
  padding: 2px 6px;
}

.ocr-fullscreen-preview__footer {
  flex-shrink: 0;
  padding: 12px 16px calc(12px + env(safe-area-inset-bottom));
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.55), transparent);
  color: #fff;
  z-index: 2;
}

.ocr-fullscreen-preview__zoom-btns {
  display: flex;
  align-items: center;
  gap: 8px;
}

.ocr-fullscreen-preview__scale {
  min-width: 44px;
  text-align: center;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.85);
}

/* 图片预览提示 */
.image-preview-hint {
  position: absolute;
  bottom: 8px;
  right: 8px;
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  background: rgba(0, 0, 0, 0.6);
  color: #fff;
  font-size: 11px;
  border-radius: 12px;
  -webkit-backdrop-filter: blur(4px);
  backdrop-filter: blur(4px);
  opacity: 0.8;
  transition: opacity 0.3s;
}

.ocr-selection__image-preview:hover .image-preview-hint {
  opacity: 1;
}

.ocr-dialog__content {
  margin-top: 20px;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding-bottom: 20px;  /* 选项区域底部留出空隙 */
}

.ocr-word-item {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 6px 12px;
  background: #f7f8fa;
  border: 1.5px solid #ebedf0;
  border-radius: 16px;
  font-size: 13px;
  color: #323233;
  cursor: pointer;
  transition: all 0.2s;
  user-select: none;
  line-height: 1.2;
}

.ocr-word-item:active {
  transform: scale(0.95);
}

.ocr-word-item--active {
  background: #e8f3ff;
  border-color: #1989fa;
  color: #1989fa;
  font-weight: 500;
}

/* 手动模式文本区域 */
.ocr-manual-text {
  padding: 0;
}

.ocr-manual-hint {
  margin: 0 0 12px 0;
  padding: 12px;
  background: #fff7e6;
  border: 1px solid #ffe7ba;
  border-radius: 4px;
  font-size: 13px;
  line-height: 1.6;
  color: #ed6a0c;
  display: flex;
  align-items: flex-start;
  gap: 6px;
}

.ocr-manual-hint::before {
  content: "💡";
  font-size: 16px;
  flex-shrink: 0;
}

.ocr-manual-text__content {
  padding: 16px;
  background: #fff;
  border-radius: 4px;
  border: 1px solid #ebedf0;
  min-height: 120px;
  /* max-height: 300px;
  overflow-y: auto; */
  font-size: 14px;
  line-height: 1.8;
  color: #323233;
  white-space: pre-wrap;
  word-break: break-all;
  margin-bottom: 16px;
  user-select: text;
  -webkit-user-select: text;
}

.ocr-copy-btn {
  width: 100%;
}

.ocr-dialog__footer {
  flex-shrink: 0;
  padding-top: 12px;
  border-top: 1px solid #ebedf0;
  background: #fff;
  position: sticky;
  bottom: 0;
}
</style>
